import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { XMarkIcon, CheckIcon, SparklesIcon, PencilSquareIcon, LinkIcon, ClipboardDocumentIcon, UserGroupIcon, LockClosedIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';
import logger from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';

import { libraryApi } from '../../lib/api/libraryApi';
import { normalizeQuestionType } from '../../constants/questionTypes';
import AssessmentCreate from './AssessmentCreate';
import AssessmentBuild from './AssessmentBuild';
import ParagraphReadingCreate from './ParagraphReadingCreate';
import MultiModuleCreate from './MultiModuleCreate';
import RubricBasedAssessmentCreate from './RubricBasedAssessmentCreate';
import AssessmentProctoringStep, {
  defaultProctoringState,
} from './AssessmentProctoringStep';
import AssessmentInvite from './AssessmentInvite';
import {
  CODING_TEMPLATES,
  CODING_TEMPLATE_PACK,
  CODING_TEMPLATE_PACK_OPTIONS,
} from './codingAssignmentTemplates';
import {
  PASSAGE_READING_TEMPLATES,
  PASSAGE_READING_PACKS,
} from './passageReadingTemplates';

const CREATION_MODE = {
  AI: 'ai',
  MANUAL: 'manual',
  ADVANCED: 'advanced',
};

const ADVANCED_FIXED_PROCTORING_STATE = {
  ...defaultProctoringState,
  enableFullScreenMonitoring: true,
  fullScreenToleranceLimit: 3,
  enableTabSwitchMonitoring: true,
  tabSwitchToleranceLimit: 2,
  enableCopyPasteBlocking: true,
  copyPasteToleranceLimit: 1,
  enableMouseMonitoring: true,
  mouseMonitoringToleranceLimit: 2,
};

const ADVANCED_TEMPLATE_CARD_DURATION_LABEL = '10mins';
const ADVANCED_TASK_DURATION_MINUTES = 10;
const ADVANCED_PASSING_SCORE = 60;
const DEFAULT_CODING_LANGUAGES = ['nodejs', 'javascript', 'python'];
const PASSAGE_READING_DURATION_MINUTES = 3;
const PASSAGE_READING_PASSING_SCORE = 70;
const PERSONALITY_DURATION_MINUTES = 20; // background cap; IPIP sets no time limit
const PERSONALITY_DEFAULT_TITLE = 'Work Style Profile';

const ADVANCED_ASSIGNMENT_OPTIONS = [
  { id: 'coding', label: 'Coding Assignment', enabled: true, description: 'Automated coding challenge with predefined templates.' },
  { id: 'passage-reading', label: 'Pronunciation', enabled: true, featureKey: 'passage_reading', description: 'Evaluate communication skills through spoken passage reading.' },
  { id: 'paragraph-reading', label: 'Paragraph Reading', enabled: true, featureKey: 'paragraph_reading', description: 'Evaluate comprehension and grasping ability through paragraph reading.' },
  { id: 'multi-module', label: 'Multi-Module Assessment', enabled: true, featureKey: 'multi_module_assessment', description: 'Combine multiple assessments into a sequential multi-module test with independent timers and scoring.' },
  { id: 'rubric-based', label: 'Rubric-Based Assessment', enabled: true, featureKey: 'rubric_based_assessment', description: 'Create modules and define dynamic shuffle logic with rubric scoring.' },
  // The fixed IPIP-50 questionnaire, no question editing. On unless the org switches it off.
  { id: 'personality', label: 'Personality Profile (Big Five)', enabled: true, featureKey: 'personality_assessment', description: 'Standard 50-statement Big Five personality test (~10 min). Guides interviews; never ranks or rejects.' },
  { id: 'tally', label: 'Tally - Invoice Processing Assignment', enabled: false, description: 'Coming soon' },
  { id: 'excel', label: 'Excel Sheet Analysis Task', enabled: false, description: 'Coming soon' },
  { id: 'dataset', label: 'Dataset Analysis Assignment', enabled: false, description: 'Coming soon' },
  { id: 'cold-email', label: 'Cold Email Writing Task', enabled: false, description: 'Coming soon' },
];

const CREATOR_CHOICE = {
  SELF: 'self',
  EXTERNAL: 'external',
};

const baseSteps = [
  { id: 0, label: 'Choose Method' },
  { id: 1, label: 'Details' },
  { id: 2, label: 'Edit Questions' },
  { id: 3, label: 'Proctoring' },
  { id: 4, label: 'Invite', description: 'Share & send' },
];

const CreationModeStep = ({ onSelectMode, phase = 'method', onSelectCategory }) => {
  if (phase === 'type') {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl w-full">
          <button
            type="button"
            data-testid="standard-assessment-btn"
            onClick={() => onSelectCategory?.('standard')}
            className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
          >
            <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#007BFF]/10 to-[#007BFF]/5 mb-4 group-hover:from-[#007BFF]/20 group-hover:to-[#007BFF]/10 transition-colors">
              <PencilSquareIcon className="w-7 h-7 text-[#007BFF]" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Standard Assessment
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Build question-based assessments with AI or from scratch. Supports MCQs, True/False, Short Answer, Essay, and more.
            </p>
            <ul className="space-y-2.5 text-sm text-gray-500">
              <li className="flex items-center gap-2">
                <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>AI-generated questions from a Job Description</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>MCQ, True/False, Short Answer &amp; Essay</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Fully editable &amp; customizable</span>
              </li>
            </ul>
          </button>

          <button
            type="button"
            data-testid="advanced-assessment-btn"
            onClick={() => onSelectCategory?.('advanced')}
            className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
          >
            <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-purple-100 mb-4 group-hover:bg-purple-200 transition-colors">
              <SparklesIcon className="w-7 h-7 text-purple-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Advanced Assessment
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Hands-on task-based assessments across domains including coding challenges, data analysis, accounting, email writing &amp; more.
            </p>
            <ul className="space-y-2.5 text-sm text-gray-500">
              <li className="flex items-center gap-2">
                <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Coding tasks in Node.js, JavaScript &amp; Python</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Excel, Tally &amp; dataset analysis tasks</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Cold email &amp; domain-specific assignments</span>
              </li>
            </ul>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl w-full">
        <button
          type="button"
          data-testid="create-with-ai-btn"
          onClick={() => onSelectMode(CREATION_MODE.AI)}
          className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
        >
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#007BFF] text-white">
              Recommended
            </span>
          </div>
          <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#007BFF]/10 to-[#007BFF]/5 mb-4 group-hover:from-[#007BFF]/20 group-hover:to-[#007BFF]/10 transition-colors">
            <SparklesIcon className="w-7 h-7 text-[#007BFF]" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Create with AI
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Upload a Job Description and let our AI generate relevant assessment questions automatically.
          </p>
          <ul className="space-y-2.5 text-sm text-gray-500">
            <li className="flex items-center gap-2">
              <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span>AI-generated questions from JD</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span>Customizable question types</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span>Edit and refine as needed</span>
            </li>
          </ul>
        </button>

        <button
          type="button"
          data-testid="create-manually-btn"
          onClick={() => onSelectMode(CREATION_MODE.MANUAL)}
          className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
        >
          <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gray-100 mb-4 group-hover:bg-[#007BFF]/10 transition-colors">
            <PencilSquareIcon className="w-7 h-7 text-gray-600 group-hover:text-[#007BFF]" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Create Manually
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Build your assessment from scratch by adding and configuring questions yourself.
          </p>
          <ul className="space-y-2.5 text-sm text-gray-500">
            <li className="flex items-center gap-2">
              <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span>Full control over questions</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span>No job description required</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span>Add questions one by one</span>
            </li>
          </ul>
        </button>
      </div>
    </div>
  );
};

const AdvancedAssessmentStep = ({
  registerContinue,
  onValidityChange,
  onConfigured,
  modalBackRef,
  onFlowStepChange,
  onPrimaryLabelChange,
  onPassageReadingSelected,
  onParagraphReadingSelected,
  onMultiModuleSelected,
  onRubricBasedSelected,
}) => {
  const [flowStep, setFlowStep] = useState(0);
  const [selectedAssignmentType, setSelectedAssignmentType] = useState(null);
  const [codingTemplatePack, setCodingTemplatePack] = useState(CODING_TEMPLATE_PACK.EXISTING);
  const [passageReadingPack, setPassageReadingPack] = useState('business');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [perTemplateConfig, setPerTemplateConfig] = useState({});
  const [assessmentTitle, setAssessmentTitle] = useState('');
  // Kept apart from assessmentTitle, which an effect clears whenever no templates are selected.
  const [personalityTitle, setPersonalityTitle] = useState(PERSONALITY_DEFAULT_TITLE);
  const [isPassageReadingModeActive, setIsPassageReadingModeActive] = useState(false);

  // Read feature flags from AuthContext (bundled with getMe response)
  const { user } = useAuth();
  const filteredAssignmentOptions = useMemo(() =>
    ADVANCED_ASSIGNMENT_OPTIONS.filter((opt) => {
      if (!opt.featureKey) return true;
      return user?.features?.[opt.featureKey] !== false;
    }),
    [user?.features]
  );

  useEffect(() => {
    setSelectedTemplateIds([]);
    setPerTemplateConfig({});
  }, [codingTemplatePack, passageReadingPack, selectedAssignmentType]);

  const isCoding = selectedAssignmentType === 'coding';
  const isPassageReading = selectedAssignmentType === 'passage-reading';

  const isValid =
    isPassageReadingModeActive
      ? true // Skip validation when Passage Reading mode is active
      : flowStep === 0
        ? Boolean(selectedAssignmentType) &&
          (selectedAssignmentType !== 'personality' || Boolean(personalityTitle.trim()))
        : flowStep === 1
          ? selectedTemplateIds.length > 0
          : Boolean(
            assessmentTitle.trim() &&
            selectedTemplateIds.length > 0 &&
            (isPassageReading || selectedTemplateIds.every(
              (id) => (perTemplateConfig[id]?.allowedLanguages || []).length > 0
            ))
          );

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    onFlowStepChange?.(flowStep);
  }, [flowStep, onFlowStepChange]);

  useEffect(() => {
    onPrimaryLabelChange?.(
      flowStep === 2 || selectedAssignmentType === 'personality' ? 'Create assessment' : 'Continue'
    );
  }, [flowStep, onPrimaryLabelChange, selectedAssignmentType]);

  useEffect(() => {
    if (!modalBackRef) return;
    modalBackRef.current = () => {
      if (flowStep === 2) {
        setFlowStep(1);
        return true;
      }
      if (flowStep === 1) {
        setFlowStep(0);
        return true;
      }
      return false;
    };
  }, [flowStep, modalBackRef]);

  useEffect(() => {
    if (selectedTemplateIds.length === 0) {
      setAssessmentTitle('');
      return;
    }
    const templates = isPassageReading ? PASSAGE_READING_TEMPLATES : CODING_TEMPLATES;
    const first = templates.find((t) => t.id === selectedTemplateIds[0]);
    if (!first) return;
    setAssessmentTitle((prev) =>
      prev.trim() === '' ? `${first.title} Assessment` : prev
    );
  }, [selectedTemplateIds, selectedAssignmentType]);

  const toggleTemplateSelected = (templateId) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(templateId) ? prev.filter((id) => id !== templateId) : [...prev, templateId]
    );
  };

  useEffect(() => {
    setPerTemplateConfig((c) => {
      const next = {};
      for (const id of selectedTemplateIds) {
        next[id] = c[id] || { allowedLanguages: [...DEFAULT_CODING_LANGUAGES] };
      }
      return next;
    });
  }, [selectedTemplateIds]);

  const setLanguagesForTemplate = (templateId, updater) => {
    setPerTemplateConfig((c) => ({
      ...c,
      [templateId]: {
        ...c[templateId],
        allowedLanguages: updater(c[templateId]?.allowedLanguages || []),
      },
    }));
  };

  useEffect(() => {
    if (typeof registerContinue !== 'function') return;
    registerContinue(async () => {
      if (!isValid) return false;

      // Personality: the server builds the fixed 50 statements from assessmentKind.
      if (selectedAssignmentType === 'personality') {
        const personalityFormData = {
          title: personalityTitle.trim().slice(0, 100),
          duration: PERSONALITY_DURATION_MINUTES,
          passingScore: 0,
          role: 'HR',
          additionalContext: '',
          assessmentTypes: ['Behavioral'],
          questionTypes: ['Personality Statement'],
          jobPosting: null,
          category: 'personality',
          difficulty: 'easy',
          assessmentKind: 'personality-ipip50',
        };
        onConfigured?.({ formData: personalityFormData, questions: [] });
        return { finish: true, formData: personalityFormData, questions: [] };
      }

      if (flowStep === 0) {
        setFlowStep(1);
        return false;
      }

      if (flowStep === 1) {
        setFlowStep(2);
        return false;
      }

      const questions = [];
      const templates = isPassageReading ? PASSAGE_READING_TEMPLATES : CODING_TEMPLATES;

      for (const id of selectedTemplateIds) {
        const template = templates.find((t) => t.id === id);
        if (!template) continue;

        if (isPassageReading) {
          questions.push({
            title: template.title,
            content: template.prompt,
            type: 'passage-reading',
            points: 100,
            correctAnswer: '',
            options: [],
            passageMeta: {
              passageText: template.passageText,
              difficulty: template.difficulty,
              wordCount: template.wordCount,
              estimatedDuration: template.duration * 60,
              language: template.language,
              category: template.category,
            },
          });
        } else {
          const langs = perTemplateConfig[id]?.allowedLanguages || [];
          if (langs.length === 0) continue;
          const problemStatement = template.problemStatement || template.prompt || '';
          const sampleInput = template.sampleInput || '';
          const sampleOutput = template.sampleOutput || '';
          questions.push({
            title: template.title,
            content: problemStatement,
            type: 'code',
            points: 100,
            correctAnswer: '',
            options: [],
            codingMeta: {
              templateId: template.id,
              difficulty: template.difficulty,
              allowedLanguages: langs,
              starterByLanguage: template.starterByLanguage,
              expectedByLanguage: template.expectedByLanguage || {},
              problemStatement,
              sampleInput,
              sampleOutput,
              evaluationLogic: template.evaluationLogic || [],
            },
          });
        }
      }

      if (questions.length === 0) return false;

      const firstTemplate = templates.find((t) => t.id === selectedTemplateIds[0]);

      const assembledFormData = {
        title: assessmentTitle.trim(),
        duration: selectedTemplateIds.length * (isPassageReading ? PASSAGE_READING_DURATION_MINUTES : ADVANCED_TASK_DURATION_MINUTES),
        passingScore: isPassageReading ? PASSAGE_READING_PASSING_SCORE : ADVANCED_PASSING_SCORE,
        role: isPassageReading ? 'Communication' : 'IT',
        additionalContext: '',
        assessmentTypes: isPassageReading ? ['Communication'] : ['Technical'],
        questionTypes: isPassageReading ? ['Passage Reading'] : ['Code'],
        jobPosting: null,
        category: isPassageReading ? 'communication' : 'software-engineering',
        difficulty: firstTemplate?.difficulty?.toLowerCase() || 'intermediate',
      };

      onConfigured?.({ formData: assembledFormData, questions });

      // Return payload so the parent can finish publish in the same turn; React state
      // from onConfigured is not updated until after this handler returns (stale closure).
      return { finish: true, formData: assembledFormData, questions };
    });
  }, [
    registerContinue,
    isValid,
    flowStep,
    onConfigured,
    selectedTemplateIds,
    perTemplateConfig,
    assessmentTitle,
    selectedAssignmentType,
    personalityTitle,
  ]);

  const languageLabel = (lang) =>
    lang === 'nodejs'
      ? 'NodeJs'
      : lang === 'javascript'
        ? 'JavaScript'
        : lang === 'python'
          ? 'Python'
          : 'Java';

  return (
    <div className="max-w-4xl mx-auto">
      {flowStep === 0 && (
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-gray-900">Select Advanced Assessment Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredAssignmentOptions.map((option) => {
              const selected = selectedAssignmentType === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={!option.enabled}
                  onClick={() => {
                    if (!option.enabled) return;
                    if (option.id === 'passage-reading') {
                      setIsPassageReadingModeActive(true);
                      onPassageReadingSelected?.();
                      return;
                    }
                    if (option.id === 'paragraph-reading') {
                      onParagraphReadingSelected?.();
                      return;
                    }
                    if (option.id === 'multi-module') {
                      onMultiModuleSelected?.();
                      return;
                    }
                    if (option.id === 'rubric-based') {
                      onRubricBasedSelected?.();
                      return;
                    }
                    if (option.id === 'personality') {
                      // Nothing to configure: stay on this step; Continue creates it.
                      setSelectedAssignmentType(option.id);
                      return;
                    }
                    setSelectedAssignmentType(option.id);
                    setFlowStep(1);
                  }}
                  className={`text-left rounded-xl border p-4 transition-all ${!option.enabled
                      ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                      : selected
                        ? 'border-[#007BFF] bg-[#007BFF]/5'
                        : 'border-gray-200 hover:border-[#007BFF]/40'
                    }`}
                >
                  <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                </button>
              );
            })}
          </div>
          {selectedAssignmentType === 'personality' && (
            <div data-testid="personality-assessment-summary" className="rounded-xl border border-[#007BFF]/30 bg-[#007BFF]/5 p-4 text-sm text-gray-700 space-y-1">
              <p className="font-semibold text-gray-900">Personality Profile (Big Five)</p>
              <div className="pt-1 pb-2">
                <label htmlFor="personality-assessment-title" className="block text-xs font-medium text-gray-700 mb-1">
                  Assessment name
                </label>
                <input
                  id="personality-assessment-title"
                  data-testid="personality-title-input"
                  value={personalityTitle}
                  maxLength={100}
                  onChange={(e) => setPersonalityTitle(e.target.value)}
                  placeholder={PERSONALITY_DEFAULT_TITLE}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                />
                <p className="text-[11px] text-gray-500 mt-1">Recruiters and candidates see this name.</p>
              </div>
              <p>Candidates see it as the <span className="font-medium">Work Style Questionnaire</span>: 50 statements, 10 per page, about 10 minutes with no visible timer.</p>
              <p>You get a profile on Extraversion, Agreeableness, Conscientiousness, Emotional Stability and Intellect/Imagination. It never rejects or ranks a candidate on its own.</p>
              <p className="text-xs text-gray-500">The statements are fixed and cannot be edited. Based on the IPIP Big-Five Factor Markers (Goldberg, 1992), a public-domain research instrument.</p>
            </div>
          )}
        </div>
      )}

      {flowStep === 1 && (
        <div className="space-y-3">
          {isPassageReading ? (
            <>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Choose Pronunciation Template</h3>
                <p className="text-xs text-gray-500 mt-1">Select one or more passages for candidates to read aloud.</p>
              </div>
              <div>
                <label htmlFor="passage-reading-pack" className="block text-xs font-medium text-gray-700 mb-1">
                  Passage category
                </label>
                <select
                  id="passage-reading-pack"
                  value={passageReadingPack}
                  onChange={(e) => setPassageReadingPack(e.target.value)}
                  className="w-full md:max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
                >
                  {PASSAGE_READING_PACKS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {PASSAGE_READING_TEMPLATES.filter((t) => t.pack === passageReadingPack).map((template) => {
                  const selected = selectedTemplateIds.includes(template.id);
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => toggleTemplateSelected(template.id)}
                      className={`text-left rounded-xl border p-4 transition-all ${selected
                          ? 'border-[#007BFF] bg-[#007BFF]/5 ring-1 ring-[#007BFF]/20'
                          : 'border-gray-200 hover:border-[#007BFF]/40'
                        }`}
                    >
                      <div className="flex items-start justify-between">
                        <p className="text-sm font-semibold text-gray-900 leading-snug">{template.title}</p>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                          {template.duration} min
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{template.difficulty} • {template.wordCount} words</p>
                      <p className="text-xs text-gray-600 mt-2 italic line-clamp-2">"{template.passageText.slice(0, 100)}..."</p>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Choose Coding Assignment Template</h3>
                <p className="text-xs text-gray-500 mt-1">Select one or more templates. You can review and adjust languages before creating.</p>
              </div>
              <div>
                <label htmlFor="coding-template-pack" className="block text-xs font-medium text-gray-700 mb-1">
                  Template library
                </label>
                <select
                  id="coding-template-pack"
                  value={codingTemplatePack}
                  onChange={(e) => setCodingTemplatePack(e.target.value)}
                  className="w-full md:max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
                >
                  {CODING_TEMPLATE_PACK_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CODING_TEMPLATES.filter((t) => t.pack === codingTemplatePack).map((template) => {
                  const selected = selectedTemplateIds.includes(template.id);
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => toggleTemplateSelected(template.id)}
                      className={`text-left rounded-xl border p-4 transition-all ${selected
                          ? 'border-[#007BFF] bg-[#007BFF]/5 ring-1 ring-[#007BFF]/20'
                          : 'border-gray-200 hover:border-[#007BFF]/40'
                        }`}
                    >
                      <p className="text-sm font-semibold text-gray-900 leading-snug">{template.title}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                          Duration {ADVANCED_TEMPLATE_CARD_DURATION_LABEL}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                          {template.difficulty}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 line-clamp-2">{template.prompt}</p>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {flowStep === 2 && selectedTemplateIds.length > 0 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {isPassageReading ? 'Review passage readings' : 'Review coding tasks'}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {selectedTemplateIds.length} {isPassageReading ? 'passage' : 'task'}
              {selectedTemplateIds.length !== 1 ? 's' : ''} selected · Duration{' '}
              {isPassageReading ? PASSAGE_READING_DURATION_MINUTES : ADVANCED_TASK_DURATION_MINUTES} min each
            </p>
          </div>

          <div>
            <label htmlFor="advanced-assessment-title" className="block text-sm font-medium text-gray-700 mb-1">
              This title
            </label>
            <input
              id="advanced-assessment-title"
              data-testid="assessment-title-input"
              value={assessmentTitle}
              onChange={(e) => setAssessmentTitle(e.target.value)}
              placeholder="Assessment title"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div className="space-y-2 max-h-[min(420px,55vh)] overflow-y-auto pr-1">
            {selectedTemplateIds.map((templateId) => {
              const templates = isPassageReading ? PASSAGE_READING_TEMPLATES : CODING_TEMPLATES;
              const template = templates.find((t) => t.id === templateId);
              if (!template) return null;

              if (isPassageReading) {
                return (
                  <div
                    key={templateId}
                    className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <p className="text-sm font-semibold text-gray-900 leading-snug">{template.title}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                        {template.duration} min
                      </span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                        {template.difficulty}
                      </span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                        {template.wordCount} words
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 line-clamp-2 italic">&ldquo;{template.passageText}&rdquo;</p>
                  </div>
                );
              }

              const langs = perTemplateConfig[templateId]?.allowedLanguages || DEFAULT_CODING_LANGUAGES;
              return (
                <div
                  key={templateId}
                  className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <p className="text-sm font-semibold text-gray-900 leading-snug">{template.title}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                      Duration {ADVANCED_TEMPLATE_CARD_DURATION_LABEL}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                      {template.difficulty}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{template.prompt}</p>
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-[11px] font-medium text-gray-600 mb-1.5">Languages</p>
                    <div className="flex flex-wrap gap-1.5">
                      {DEFAULT_CODING_LANGUAGES.map((lang) => {
                        const checked = langs.includes(lang);
                        return (
                          <button
                            key={lang}
                            type="button"
                            onClick={() =>
                              setLanguagesForTemplate(templateId, (prev) =>
                                prev.includes(lang)
                                  ? prev.filter((l) => l !== lang)
                                  : [...prev, lang]
                              )
                            }
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border ${checked
                                ? 'bg-[#007BFF] text-white border-[#007BFF]'
                                : 'bg-white text-gray-700 border-gray-300'
                              }`}
                          >
                            {languageLabel(lang)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AssessmentCreateFlowModal = ({
  jobPostingId,
  screeningName = '',
  onClose,
  skipInviteStep = false,
  hasNextStep = false,
  libraryTestId = null, // When provided, import questions from this library test
  viewOnly = false, // When true, show read-only view of assessment questions (no editing allowed)
  viewTestId = null, // Test ID to view in read-only mode
  editLibraryTestId = null, // When set, open directly at "Edit Questions" step for an existing library Test
  isLibraryFlow = false, // When true, show library selection as first step
  hideExternalCreationLink = false, // When true, hide external creation link UI (public external flow)
  enableCreatorSelection = false, // When true, add "Who creates?" step (assessments library create)
  libraryOrgId = null, // When provided, fetch library content for a specific organisation (internal admin views)
  forceShowVisibilityStep = false, // When true, force the visibility step to show (external flows)
}) => {
  const libraryOnly = Boolean(
    skipInviteStep && !jobPostingId && !libraryTestId && !viewOnly && !editLibraryTestId && !isLibraryFlow
  );

  // Auth context — used for org-level screening rules (e.g. only show multi-module assessments)
  const { user } = useAuth();

  // Library flow state - declare activeLibraryTestId early as it's used in computed values below
  const [activeLibraryTestId, setActiveLibraryTestId] = useState(libraryTestId);
  const [libraryAssessments, setLibraryAssessments] = useState([]);
  const [loadingLibraryAssessments, setLoadingLibraryAssessments] = useState(false);
  const [selectedLibraryAssessment, setSelectedLibraryAssessment] = useState(null);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [libraryCategoryFilter, setLibraryCategoryFilter] = useState('');
  const [libraryDifficultyFilter, setLibraryDifficultyFilter] = useState('');

  // If jobPostingId exists (screening flow), skip "Choose Method" step and default to AI mode
  // If libraryTestId exists, skip "Choose Method" and "Details" steps, go directly to Edit Questions
  // If viewOnly exists, skip to Edit Questions step in read-only mode
  // If isLibraryFlow, show library selection as step 1
  // If no jobPostingId (library flow), show "Choose Method" step (step 0)
  const isEditLibrary = Boolean(editLibraryTestId);
  const showChooseMethodStep =
    !jobPostingId && !libraryTestId && !viewOnly && !isEditLibrary && !isLibraryFlow;
  const isLibraryImport = Boolean(libraryTestId) || Boolean(activeLibraryTestId);
  const isViewOnly = Boolean(viewOnly && viewTestId);
  const showCreatorSelectionStep =
    enableCreatorSelection && showChooseMethodStep && libraryOnly && !hideExternalCreationLink;

  const [creationMode, setCreationMode] = useState(
    jobPostingId || libraryTestId || isViewOnly || isEditLibrary || isLibraryFlow
      ? CREATION_MODE.AI
      : null
  );
  const [currentStep, setCurrentStep] = useState(
    showCreatorSelectionStep
      ? -1
      : forceShowVisibilityStep
        ? -2
        : libraryTestId || isViewOnly || isEditLibrary
          ? 2
          : isLibraryFlow
            ? 1
            : jobPostingId
              ? 1
              : 0
  );
  const [creatorChoice, setCreatorChoice] = useState(null);
  const [assessmentCategory, setAssessmentCategory] = useState(null);
  const step0Phase = assessmentCategory === 'standard' ? 'method' : 'type';
  const [libraryImportLoading, setLibraryImportLoading] = useState(false);
  const [viewOnlyLoading, setViewOnlyLoading] = useState(false);
  const [editLibraryLoading, setEditLibraryLoading] = useState(false);
  const [editLibrarySaving, setEditLibrarySaving] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);
  const [didCreate, setDidCreate] = useState(false);
  const [aiSections, setAiSections] = useState(null);
  const [assessmentFormData, setAssessmentFormData] = useState(null);
  const [proctoringState, setProctoringState] = useState(defaultProctoringState);
  const [questionsData, setQuestionsData] = useState([]);
  const [questionShuffleEnabled, setQuestionShuffleEnabled] = useState(false);
  // Tracks flows where questions are already persisted when the assessment is first created
  const [questionsCreatedWithAssessment, setQuestionsCreatedWithAssessment] = useState(false);
  const [assessmentDuration, setAssessmentDuration] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [regeneratingQuestions, setRegeneratingQuestions] = useState(false);
  const [buildRefreshKey, setBuildRefreshKey] = useState(0);
  const [moduleMapState, setModuleMapState] = useState({});

  const [detailsSubmit, setDetailsSubmit] = useState(null);
  const [buildContinue, setBuildContinue] = useState(null);
  const [buildSaveQuestions, setBuildSaveQuestions] = useState(null);
  const [inviteFinish, setInviteFinish] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [pendingGenerationCount, setPendingGenerationCount] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState(null);
  const [showSaveToLibraryConfirm, setShowSaveToLibraryConfirm] = useState(false);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [hasSavedToLibrary, setHasSavedToLibrary] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [externalCreationLink, setExternalCreationLink] = useState('');
  const [externalLinkCopied, setExternalLinkCopied] = useState(false);
  // Visibility step state
  const [visibilityLevel, setVisibilityLevel] = useState(null);
  const [selectedSharedDepartmentIds, setSelectedSharedDepartmentIds] = useState(new Set());
  const [accessOptions, setAccessOptions] = useState(null);
  const [loadingAccessOptions, setLoadingAccessOptions] = useState(false);
  const [showVisibilityStep, setShowVisibilityStep] = useState(
    (enableCreatorSelection && showChooseMethodStep && libraryOnly) || forceShowVisibilityStep
  );
  const [advancedContinue, setAdvancedContinue] = useState(null);
  const [isAdvancedValid, setIsAdvancedValid] = useState(false);
  const [advancedConfigured, setAdvancedConfigured] = useState(false);
  const advancedModalBackRef = useRef(() => false);
  const [advancedFlowStep, setAdvancedFlowStep] = useState(0);
  const [advancedPrimaryLabel, setAdvancedPrimaryLabel] = useState('Continue');
  const [isPassageReadingMode, setIsPassageReadingMode] = useState(false);
  const [isParagraphReadingMode, setIsParagraphReadingMode] = useState(false);
  const [isMultiModuleMode, setIsMultiModuleMode] = useState(false);
  const [isRubricBasedMode, setIsRubricBasedMode] = useState(false);

  const isAdvancedMode = creationMode === CREATION_MODE.ADVANCED || isPassageReadingMode || isParagraphReadingMode || isMultiModuleMode || isRubricBasedMode;
  const isCodingAssignmentMode = isAdvancedMode && !isPassageReadingMode && !isParagraphReadingMode && !isMultiModuleMode && !isRubricBasedMode;
  const isManualMode = creationMode === CREATION_MODE.MANUAL;

  useEffect(() => {
    if (!isAdvancedMode) return;
    setProctoringState((prev) => ({
      ...prev,
      ...ADVANCED_FIXED_PROCTORING_STATE,
    }));
  }, [isAdvancedMode]);

  // Fetch access options (departments) for visibility step
  useEffect(() => {
    if (!showVisibilityStep) return;
    let cancelled = false;
    const fetchOptions = async () => {
      setLoadingAccessOptions(true);
      try {
        const res = await axios.get('/api/library/access-options');
        if (cancelled) return;
        setAccessOptions(res.data);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch access options:', err);
        }
      } finally {
        if (!cancelled) {
          setLoadingAccessOptions(false);
        }
      }
    };
    fetchOptions();
    return () => { cancelled = true; };
  }, [showVisibilityStep]);

  const toggleSharedDepartment = useCallback((deptId) => {
    setSelectedSharedDepartmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return next;
    });
  }, []);

  const isExternalLinkStep =
    showCreatorSelectionStep && creatorChoice === CREATOR_CHOICE.EXTERNAL && currentStep === -2;

  const handleGenerateExternalCreationLink = useCallback(async () => {
    try {
      const res = await axios.post('/api/assessments/external-create-links');
      const code = res.data?.code;
      if (!code) {
        throw new Error('No code returned');
      }
      const link = `${window.location.origin}/external/assessment-create/${code}`;
      setExternalCreationLink(link);

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setExternalLinkCopied(true);
        setTimeout(() => setExternalLinkCopied(false), 2000);
        toast.success('External creation link copied.');
      } else {
        toast.success('External creation link generated.');
      }
    } catch (error) {
      console.error('External create link error:', error);
      toast.error('Failed to generate external link.');
    }
  }, []);

  // Fetch library assessments when in library flow
  useEffect(() => {
    if (!isLibraryFlow) return;

    const fetchLibraryAssessments = async () => {
      try {
        setLoadingLibraryAssessments(true);
        const response = await libraryApi.getAssessmentsStats({ page: 1, limit: 100, orgId: libraryOrgId });
        setLibraryAssessments(response.assessments || []);
      } catch (error) {
        console.error('Error fetching library assessments:', error);
        toast.error('Failed to load assessments from library');
      } finally {
        setLoadingLibraryAssessments(false);
      }
    };

    fetchLibraryAssessments();
  }, [isLibraryFlow, libraryOrgId]);

  // Import questions from library test when libraryTestId is provided or when selected from library flow
  useEffect(() => {
    if (!activeLibraryTestId) return;

    const importLibraryTest = async () => {
      setLibraryImportLoading(true);
      try {
        // Fetch the library test with populated questions
        const test = await libraryApi.getLibraryAssessmentById(activeLibraryTestId, { orgId: libraryOrgId });

        if (!test) {
          toast.error('Failed to load library assessment');
          return;
        }

        // Multi-module library tests have no own questions — their content lives in the
        // child Tests referenced by test.modules. Create the parent assessment with the
        // module references intact instead of flattening the (empty) questions array.
        if (test.isRubricBased) {
          // Pre-process modules to extract questions from child tests BEFORE creating the payload
          test.modules = (test.modules || []).map(m => {
            const moduleTest = m.moduleTestId || m.libraryTestId;
            const sourceQuestions = (m.questionBank && m.questionBank.length > 0) ? m.questionBank : (moduleTest?.questions || []);
            const qs = sourceQuestions.map(qItem => {
              const q = qItem.question || qItem;
              const parseType = (rawType) => {
                if (!rawType || typeof rawType !== 'string') return 'short-answer';
                const t = rawType.toLowerCase();
                if (t.includes('multiple') || t.includes('choice')) return 'multiple-choice';
                if (t.includes('true') && t.includes('false')) return 'true-false';
                if (t.includes('fill') && t.includes('blank')) return 'fill-in-blanks';
                if (t.includes('essay')) return 'essay';
                if (t.includes('code')) return 'code';
                if (t.includes('passage')) return 'passage-reading';
                if (t.includes('short') || t.includes('answer')) return 'short-answer';
                return 'short-answer';
              };
              return {
                _localId: Math.random().toString(36).substring(2, 9),
                prompt: q.title || q.text || q.content || '',
                type: parseType(q.type),
                options: q.options || [],
                correctAnswer: q.correctAnswer || '',
                points: qItem.points || q.points || 1,
                mandatory: qItem.mandatory === true,
                // Provide full question data for the backend to save
                ...q,
                title: q.title || q.text || q.content || '',
                content: q.content || q.text || q.title || '',
              };
            });
            return {
              id: m._id || Math.random().toString(36).substring(2, 9),
              moduleTestId: m.moduleTestId?._id || m.moduleTestId || m.libraryTestId,
              name: m.title || moduleTest?.title || '',
              duration: m.duration || moduleTest?.estimatedTime || 10,
              questionsToPick: m.questionsToPick !== undefined && m.questionsToPick !== null ? Number(m.questionsToPick) : 1,
              questionBank: qs,
              title: m.title,
              order: m.order,
              totalPoints: m.points || m.totalPoints,
            };
          });

          const moduleList = Array.isArray(test.modules) ? test.modules : [];
          const totalDuration =
            moduleList.reduce((sum, m) => sum + (m.duration || 0), 0) || test.estimatedTime || 30;
          const modulesPayload = moduleList.map((m, idx) => ({
            libraryTestId: m.moduleTestId || m.libraryTestId,
            order: m.order ?? idx + 1,
            title: m.name || m.title,
            duration: m.duration,
            totalPoints: m.totalPoints ?? m.points ?? (Array.isArray(m.questionBank) ? m.questionBank.reduce((sum, q) => sum + (q.points || 1), 0) : 0),
            questionsToPick: m.questionsToPick,
            questionBank: m.questionBank,
          }));

          const mmPayload = {
            title: test.title || 'Imported Assessment',
            jobPosting: jobPostingId,
            questionType: 'Technical',
            noOfQuestions: 0,
            assessmentDuration: totalDuration,
            duration: totalDuration,
            passingScore: test.passingScore || 70,
            libraryTestId: activeLibraryTestId,
            isMultiModule: false,
            isRubricBased: true,
            modules: modulesPayload,
            shuffleQuestionsEnabled:
              Boolean(test.shuffleQuestionsEnabled) || Boolean(questionShuffleEnabled),
          };

          logger.log('📤 Creating rubric-based assessment from library test:', mmPayload);
          const mmRes = await axios.post('/api/assessments', mmPayload);
          const mmId = mmRes.data?.assessment?._id;

          if (mmId) {
            setAssessmentId(mmId);
            setQuestionsData([]);
            setQuestionsCreatedWithAssessment(true);
            setImportedLibraryTest(test);
            setAssessmentFormData({
              title: test.title,
              category: test.category,
              difficulty: test.difficulty,
              duration: totalDuration,
              isMultiModule: false,
              isRubricBased: true,
              modules: test.modules,
            });
            setAssessmentDuration(totalDuration);
            logger.log('✅ Rubric-based assessment created from library test:', mmId, 'with', test.modules.length, 'modules');
            toast.success('Assessment imported successfully');
          }
          return;
        } else if (test.isMultiModule) {
          const moduleList = Array.isArray(test.modules) ? test.modules : [];
          const totalDuration =
            moduleList.reduce((sum, m) => sum + (m.duration || 0), 0) || test.estimatedTime || 30;
          const modulesPayload = moduleList.map((m, idx) => {
            // Resolve populated sub-test object (has totalPoints, numberOfQuestions, questions)
            const popMod = (typeof m.moduleTestId === 'object' && m.moduleTestId) || (typeof m.libraryTestId === 'object' && m.libraryTestId) || {};
            // Compute totalPoints from populated questions if the stored value is 0
            const resolvedTotalPoints =
              (popMod.totalPoints > 0 ? popMod.totalPoints : undefined) ??
              (Array.isArray(popMod.questions) && popMod.questions.length > 0 ? popMod.questions.reduce((sum, q) => sum + (q.points || 1), 0) : undefined) ??
              (Array.isArray(m.questionBank) ? m.questionBank.reduce((sum, q) => sum + (q.points || 1), 0) : 0);
            return {
              libraryTestId: popMod._id || m.moduleTestId || m.libraryTestId,
              order: m.order ?? idx + 1,
              title: m.title,
              duration: m.duration,
              totalPoints: resolvedTotalPoints,
              questionsToPick: m.questionsToPick,
              questionBank: m.questionBank,
            };
          });

          const mmPayload = {
            title: test.title || 'Imported Assessment',
            jobPosting: jobPostingId,
            questionType: 'Technical',
            noOfQuestions: 0,
            assessmentDuration: totalDuration,
            duration: totalDuration,
            passingScore: test.passingScore || 70,
            libraryTestId: activeLibraryTestId,
            isMultiModule: true,
            isRubricBased: false,
            modules: modulesPayload,
            shuffleQuestionsEnabled:
              Boolean(test.shuffleQuestionsEnabled) || Boolean(questionShuffleEnabled),
          };

          logger.log('📤 Creating multi-module assessment from library test:', mmPayload);
          const mmRes = await axios.post('/api/assessments', mmPayload);
          const mmId = mmRes.data?.assessment?._id;

          if (mmId) {
            setAssessmentId(mmId);
            setQuestionsData([]);
            setQuestionsCreatedWithAssessment(true);
            setImportedLibraryTest(test);
            setAssessmentFormData({
              title: test.title,
              category: test.category,
              difficulty: test.difficulty,
              duration: totalDuration,
              isMultiModule: true,
              isRubricBased: false,
              modules: modulesPayload,
            });
            setDetailsDraft({ title: test.title, category: test.category, modules: test.modules, globalDuration: totalDuration });
            setAssessmentDuration(totalDuration);
            logger.log('✅ Multi-module assessment created from library test:', mmId, 'with', modulesPayload.length, 'modules');
            toast.success('Assessment imported successfully');
          }
          return;
        }

        // Transform library test questions to the format expected by the assessment
        const flatQuestions = [];
        if (test.questions && Array.isArray(test.questions)) {
          for (const qItem of test.questions) {
            const q = qItem.question || qItem;
            if (!q) continue;

            const type = (q.type || 'short-answer').toLowerCase();
            const pmeta = q.passageMeta || qItem.passageMeta || null;
            // Paragraph reading questions are MCQ/True-False with an attached paragraph in
            // passageMeta — keep their MCQ/TF type so options and scoring stay intact.
            const isParagraphReading =
              pmeta &&
              (pmeta.isParagraphReading === true ||
                (typeof pmeta.paragraphText === 'string' && pmeta.paragraphText.trim().length > 0));
            const normalizedType = isParagraphReading
              ? (type.includes('true') ? 'true-false' : 'multiple-choice')
              : type.includes('multiple') ? 'multiple-choice'
                : type.includes('true') && type.includes('false') ? 'true-false'
                  : type.includes('essay') ? 'essay'
                    : type.includes('code') ? 'code'
                      : type.includes('passage') ? 'passage-reading'
                        : type.includes('fill') && type.includes('blank') ? 'fill-in-blanks'
                          : 'short-answer';

            const customQuestion = {
              title: q.title || q.text || 'Question',
              content: q.content || '',
              type: normalizedType,
              points: qItem.points || q.points || 1,
              skills: Array.isArray(q.skills) ? q.skills : [],
            };

            if (normalizedType === 'multiple-choice' && Array.isArray(q.options)) {
              customQuestion.options = q.options.map((opt, idx) => ({
                text: typeof opt === 'string' ? opt : (opt.text || opt.label || ''),
                isCorrect: typeof opt === 'object' ? !!opt.isCorrect : (q.correctAnswer === String.fromCharCode(97 + idx)),
              }));
              const correctIdx = customQuestion.options.findIndex(o => o.isCorrect);
              customQuestion.correctAnswer = correctIdx >= 0 ? String.fromCharCode(97 + correctIdx) : 'a';
            } else if (normalizedType === 'true-false') {
              customQuestion.options = [
                { text: 'True', isCorrect: q.correctAnswer !== 'b' && q.correctAnswer !== 'false' && q.correctAnswer !== false },
                { text: 'False', isCorrect: q.correctAnswer === 'b' || q.correctAnswer === 'false' || q.correctAnswer === false },
              ];
              customQuestion.correctAnswer = customQuestion.options[0].isCorrect ? 'a' : 'b';
            } else {
              customQuestion.correctAnswer = q.correctAnswer || q.answer || '';
            }
            if (normalizedType === 'code') {
              customQuestion.codingMeta = q.codingMeta || null;
            }
            // Preserve passageMeta for both pronunciation passages and paragraph reading.
            if (pmeta) {
              customQuestion.passageMeta = pmeta;
            }

            flatQuestions.push(customQuestion);
          }
        }

        // Create assessment from library test
        // Map category to valid questionType for assessment generation
        const validQuestionTypes = ['Technical', 'Behavioral', 'Cultural'];
        const questionType = validQuestionTypes.includes(test.category) ? test.category : 'Technical';

        const createPayload = {
          title: test.title || 'Imported Assessment',
          jobPosting: jobPostingId,
          questionType,
          noOfQuestions: flatQuestions.length,
          assessmentDuration: test.estimatedTime || 30,
          duration: test.estimatedTime || 30,
          passingScore: test.passingScore || 70,
          customQuestions: flatQuestions,
          libraryTestId: activeLibraryTestId, // Link back to library test for stats tracking
          shuffleQuestionsEnabled:
            Boolean(test.shuffleQuestionsEnabled) || Boolean(questionShuffleEnabled),
        };

        logger.log('📤 Creating assessment from library test:', createPayload);
        logger.log('📋 Test data received:', test);
        logger.log('📋 Questions extracted:', flatQuestions.length);

        const createRes = await axios.post('/api/assessments', createPayload);
        const newId = createRes.data?.assessment?._id;

        if (newId) {
          setAssessmentId(newId);
          setQuestionsData(flatQuestions);
          // For library imports we persist questions together with the assessment
          setQuestionsCreatedWithAssessment(true);
          setAssessmentFormData({
            title: test.title,
            category: test.category,
            difficulty: test.difficulty,
            duration: test.estimatedTime || 30,
          });
          setAssessmentDuration(test.estimatedTime || 30);
          logger.log('✅ Assessment created from library test:', newId);
          toast.success('Assessment imported successfully');
        }
      } catch (err) {
        console.error('Error importing library test:', err);
        console.error('Error response:', err.response?.data);

        // Show more detailed error message
        let errorMsg = 'Failed to import assessment from library';
        if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
          errorMsg = err.response.data.errors.map(e => e.msg || e.message).join(', ');
        } else if (err.response?.data?.message) {
          errorMsg = err.response.data.message;
        }
        toast.error(errorMsg);
      } finally {
        setLibraryImportLoading(false);
      }
    };

    importLibraryTest();
  }, [activeLibraryTestId, jobPostingId, libraryOrgId]);

  // Stores the raw Test doc for view-only and edit-library modes so we know if it is multi-module.
  const [viewOnlyTest, setViewOnlyTest] = useState(null);
  const isViewOnlyMultiModule = Boolean(viewOnlyTest?.isMultiModule);
  const isViewOnlyRubricBased = Boolean(viewOnlyTest?.isRubricBased);
  const [editLibraryTest, setEditLibraryTest] = useState(null);
  const isEditLibraryMultiModule = Boolean(editLibraryTest?.isMultiModule);
  const isEditLibraryRubricBased = Boolean(editLibraryTest?.isRubricBased);
  // Stores the imported library Test (screening library flow) so the View Questions
  // step can render its modules when the imported assessment is multi-module.
  const [importedLibraryTest, setImportedLibraryTest] = useState(null);
  const isLibraryImportMultiModule = Boolean(importedLibraryTest?.isMultiModule);
  const isLibraryImportRubricBased = Boolean(importedLibraryTest?.isRubricBased);

  // Load test data for view-only mode (no creation, just display)
  useEffect(() => {
    if (!isViewOnly || !viewTestId) return;

    const loadTestForViewing = async () => {
      setViewOnlyLoading(true);
      try {
        const test = await libraryApi.getLibraryAssessmentById(viewTestId, { orgId: libraryOrgId });

        if (!test) {
          toast.error('Failed to load assessment');
          return;
        }

        // Store the raw test document so we can check isMultiModule
        setViewOnlyTest(test);

        // Multi-module & rubric-based assessments have no individual questions — just module refs.
        // Skip question-flattening and let MultiModuleCreate / RubricBasedAssessmentCreate render the modules.
        if (test.isMultiModule || test.isRubricBased) {
          setAssessmentFormData({
            title: test.title,
            category: test.category || 'Advanced',
            difficulty: test.difficulty,
            duration: test.estimatedTime || 30,
            isRubricBased: test.isRubricBased || false,
          });
          setAssessmentDuration(test.estimatedTime || 30);
          
          if (test.isRubricBased) {
            test.modules = (test.modules || []).map(m => {
              const moduleTest = m.moduleTestId || m.libraryTestId;
              const sourceQuestions = (m.questionBank && m.questionBank.length > 0) ? m.questionBank : (moduleTest?.questions || []);
              const qs = sourceQuestions.map(qItem => {
                const q = qItem.question || qItem;
                const parseType = (rawType) => {
                  if (!rawType || typeof rawType !== 'string') return 'short-answer';
                  const t = rawType.toLowerCase();
                  if (t.includes('multiple') || t.includes('choice')) return 'multiple-choice';
                  if (t.includes('true') && t.includes('false')) return 'true-false';
                  if (t.includes('fill') && t.includes('blank')) return 'fill-in-blanks';
                  if (t.includes('essay')) return 'essay';
                  if (t.includes('code')) return 'code';
                  if (t.includes('passage')) return 'passage-reading';
                  if (t.includes('short') || t.includes('answer')) return 'short-answer';
                  return 'short-answer';
                };
                return {
                  _localId: Math.random().toString(36).substring(2, 9),
                  prompt: q.title || q.text || q.content || '',
                  type: parseType(q.type),
                  options: q.options || [],
                  correctAnswer: q.correctAnswer || '',
                  points: qItem.points || q.points || 1,
                  mandatory: qItem.mandatory === true,
                };
              });
              return {
                id: Math.random().toString(36).substring(2, 9),
                name: m.title || m.name || m.moduleTestId?.title || '',
                duration: m.duration || m.moduleTestId?.estimatedTime || 10,
                questionsToPick: m.questionsToPick !== undefined && m.questionsToPick !== null ? Number(m.questionsToPick) : 1,
                questionBank: qs,
              };
            });
            setDetailsDraft({ title: test.title, category: test.category || 'Advanced', modules: test.modules, globalDuration: test.estimatedTime || 30 });
          } else {
            setDetailsDraft({ title: test.title, category: test.category || 'Advanced', modules: test.modules, globalDuration: test.estimatedTime || 30 });
          }
          
          logger.log('✅ Multi-module assessment loaded for viewing:', test.title, 'with', (test.modules || []).length, 'modules');
          return;
        }

        // Transform test questions to the format expected by AssessmentBuild
        const flatQuestions = [];
        if (test.questions && Array.isArray(test.questions)) {
          for (const qItem of test.questions) {
            const q = qItem.question || qItem;
            if (!q) continue;

            const type = (q.type || 'short-answer').toLowerCase();
            const pmeta = q.passageMeta || qItem.passageMeta || null;
            // Paragraph reading questions are MCQ/True-False with an attached paragraph stored in
            // passageMeta. Keep their MCQ/TF type so options render; the paragraph is shown
            // separately. Only pronunciation passages (passageText) become 'passage-reading'.
            const isParagraphReading =
              pmeta &&
              (pmeta.isParagraphReading === true ||
                (typeof pmeta.paragraphText === 'string' && pmeta.paragraphText.trim().length > 0));
            const normalizedType = isParagraphReading
              ? type.includes('true')
                ? 'true-false'
                : 'multiple-choice'
              : type.includes('passage') || Boolean(pmeta)
                ? 'passage-reading'
                : type.includes('multiple')
                  ? 'multiple-choice'
                  : type.includes('true') && type.includes('false')
                    ? 'true-false'
                    : type.includes('essay')
                      ? 'essay'
                      : type.includes('code')
                        ? 'code'
                        : type.includes('fill') && type.includes('blank')
                          ? 'fill-in-blanks'
                          : 'short-answer';

            const customQuestion = {
              title: q.title || q.text || 'Question',
              content: q.content || '',
              type: normalizedType,
              points: qItem.points || q.points || 1,
              skills: Array.isArray(q.skills) ? q.skills : [],
            };

            if ((normalizedType === 'multiple-choice' || normalizedType === 'passage-reading') && Array.isArray(q.options)) {
              customQuestion.options = q.options.map((opt, idx) => ({
                text: typeof opt === 'string' ? opt : (opt.text || opt.label || ''),
                isCorrect: typeof opt === 'object' ? !!opt.isCorrect : (q.correctAnswer === String.fromCharCode(97 + idx)),
              }));
              const correctIdx = customQuestion.options.findIndex(o => o.isCorrect);
              customQuestion.correctAnswer = correctIdx >= 0 ? String.fromCharCode(97 + correctIdx) : 'a';
            } else if (normalizedType === 'true-false') {
              customQuestion.options = [
                { text: 'True', isCorrect: q.correctAnswer !== 'b' && q.correctAnswer !== 'false' && q.correctAnswer !== false },
                { text: 'False', isCorrect: q.correctAnswer === 'b' || q.correctAnswer === 'false' || q.correctAnswer === false },
              ];
              customQuestion.correctAnswer = customQuestion.options[0].isCorrect ? 'a' : 'b';
            } else {
              customQuestion.correctAnswer = q.correctAnswer || q.answer || '';
            }
            if (normalizedType === 'code') {
              customQuestion.codingMeta = q.codingMeta || null;
            }
            // Preserve passageMeta for both pronunciation passages and paragraph reading.
            if (pmeta) {
              customQuestion.passageMeta = pmeta;
            }

            flatQuestions.push(customQuestion);
          }
        }

        setQuestionsData(flatQuestions);
        setAssessmentFormData({
          title: test.title,
          category: test.category,
          difficulty: test.difficulty,
          duration: test.estimatedTime || 30,
        });
        setAssessmentDuration(test.estimatedTime || 30);
        logger.log('✅ Assessment loaded for viewing:', test.title, 'with', flatQuestions.length, 'questions');
      } catch (err) {
        console.error('Error loading test for viewing:', err);
        toast.error('Failed to load assessment');
      } finally {
        setViewOnlyLoading(false);
      }
    };

    loadTestForViewing();
  }, [isViewOnly, viewTestId, libraryOrgId]);

  // Load test data for edit-library mode (editable; reuses the same flattening as view-only).
  useEffect(() => {
    if (!isEditLibrary || !editLibraryTestId) return undefined;

    let cancelled = false;
    const loadTestForEditing = async () => {
      setEditLibraryLoading(true);
      try {
        const test = await libraryApi.getLibraryAssessmentById(editLibraryTestId, { orgId: libraryOrgId });
        if (cancelled) return;
        if (!test) {
          toast.error('Failed to load assessment');
          return;
        }

        // Store the raw test so we can detect multi-module, etc.
        setEditLibraryTest(test);

        // Multi-module & rubric-based assessments have no questions — show module list instead.
        if (test.isMultiModule || test.isRubricBased) {
          setQuestionsData([]);
          setAssessmentFormData({
            title: test.title,
            description: test.description || '',
            category: test.category || 'Advanced',
            difficulty: test.difficulty,
            duration: test.estimatedTime || 30,
            isRubricBased: test.isRubricBased || false,
          });
          setAssessmentDuration(test.estimatedTime || 30);
          
          if (test.isRubricBased) {
            test.modules = (test.modules || []).map(m => {
              const moduleTest = m.moduleTestId || m.libraryTestId;
              const sourceQuestions = (m.questionBank && m.questionBank.length > 0) ? m.questionBank : (moduleTest?.questions || []);
              const qs = sourceQuestions.map(qItem => {
                const q = qItem.question || qItem;
                const parseType = (rawType) => {
                  if (!rawType || typeof rawType !== 'string') return 'short-answer';
                  const t = rawType.toLowerCase();
                  if (t.includes('multiple') || t.includes('choice')) return 'multiple-choice';
                  if (t.includes('true') && t.includes('false')) return 'true-false';
                  if (t.includes('fill') && t.includes('blank')) return 'fill-in-blanks';
                  if (t.includes('essay')) return 'essay';
                  if (t.includes('code')) return 'code';
                  if (t.includes('passage')) return 'passage-reading';
                  if (t.includes('short') || t.includes('answer')) return 'short-answer';
                  return 'short-answer';
                };
                return {
                  _localId: Math.random().toString(36).substring(2, 9),
                  prompt: q.title || q.text || q.content || '',
                  type: parseType(q.type),
                  options: q.options || [],
                  correctAnswer: q.correctAnswer || '',
                  points: qItem.points || q.points || 1,
                  mandatory: qItem.mandatory === true,
                };
              });
              return {
                id: Math.random().toString(36).substring(2, 9),
                moduleTestId: moduleTest?._id || moduleTest,
                name: m.title || moduleTest?.title || '',
                duration: m.duration || moduleTest?.estimatedTime || 10,
                questionsToPick: m.questionsToPick !== undefined && m.questionsToPick !== null ? Number(m.questionsToPick) : 1,
                questionBank: qs,
              };
            });
            setDetailsDraft({ title: test.title, category: test.category || 'Advanced', modules: test.modules, globalDuration: test.estimatedTime || 30 });
          } else {
            setDetailsDraft({ title: test.title, category: test.category || 'Advanced', modules: test.modules, globalDuration: test.estimatedTime || 30 });
          }
          
          logger.log('✅ Multi-module library assessment loaded for viewing:', test.title, 'with', (test.modules || []).length, 'modules');
          return;
        }

        const flatQuestions = [];
        if (test.questions && Array.isArray(test.questions)) {
          for (const qItem of test.questions) {
            const q = qItem.question || qItem;
            if (!q) continue;

            const type = (q.type || 'short-answer').toLowerCase();
            const pmeta = q.passageMeta || qItem.passageMeta || null;
            // Paragraph reading questions are MCQ/True-False with an attached paragraph stored in
            // passageMeta. Keep their MCQ/TF type so options render; the paragraph is shown
            // separately. Only pronunciation passages (passageText) become 'passage-reading'.
            const isParagraphReading =
              pmeta &&
              (pmeta.isParagraphReading === true ||
                (typeof pmeta.paragraphText === 'string' && pmeta.paragraphText.trim().length > 0));
            const normalizedType = isParagraphReading
              ? type.includes('true')
                ? 'true-false'
                : 'multiple-choice'
              : type.includes('passage') || Boolean(pmeta)
                ? 'passage-reading'
                : type.includes('multiple')
                  ? 'multiple-choice'
                  : type.includes('true') && type.includes('false')
                    ? 'true-false'
                    : type.includes('essay')
                      ? 'essay'
                      : type.includes('code')
                        ? 'code'
                        : type.includes('fill') && type.includes('blank')
                          ? 'fill-in-blanks'
                          : 'short-answer';

            const customQuestion = {
              title: q.title || q.text || 'Question',
              content: q.content || '',
              type: normalizedType,
              points: qItem.points || q.points || 1,
              skills: Array.isArray(q.skills) ? q.skills : [],
            };

            if ((normalizedType === 'multiple-choice' || normalizedType === 'passage-reading') && Array.isArray(q.options)) {
              customQuestion.options = q.options.map((opt, idx) => ({
                text: typeof opt === 'string' ? opt : opt.text || opt.label || '',
                isCorrect:
                  typeof opt === 'object'
                    ? !!opt.isCorrect
                    : q.correctAnswer === String.fromCharCode(97 + idx),
              }));
              const correctIdx = customQuestion.options.findIndex((o) => o.isCorrect);
              customQuestion.correctAnswer =
                correctIdx >= 0 ? String.fromCharCode(97 + correctIdx) : 'a';
            } else if (normalizedType === 'true-false') {
              customQuestion.options = [
                {
                  text: 'True',
                  isCorrect:
                    q.correctAnswer !== 'b' &&
                    q.correctAnswer !== 'false' &&
                    q.correctAnswer !== false,
                },
                {
                  text: 'False',
                  isCorrect:
                    q.correctAnswer === 'b' ||
                    q.correctAnswer === 'false' ||
                    q.correctAnswer === false,
                },
              ];
              customQuestion.correctAnswer = customQuestion.options[0].isCorrect ? 'a' : 'b';
            } else {
              customQuestion.correctAnswer = q.correctAnswer || q.answer || '';
            }
            if (normalizedType === 'code') {
              customQuestion.codingMeta = q.codingMeta || null;
            }
            // Preserve passageMeta for both pronunciation passages and paragraph reading.
            if (pmeta) {
              customQuestion.passageMeta = pmeta;
            }

            flatQuestions.push(customQuestion);
          }
        }

        setQuestionsData(flatQuestions);
        setAssessmentFormData({
          title: test.title,
          description: test.description || '',
          category: test.category,
          difficulty: test.difficulty,
          duration: test.estimatedTime || 30,
        });
        setAssessmentDuration(test.estimatedTime || 30);
        setQuestionShuffleEnabled(Boolean(test.shuffleQuestionsEnabled));
        logger.log(
          '✅ Library assessment loaded for editing:',
          test.title,
          'with',
          flatQuestions.length,
          'questions'
        );
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading library test for editing:', err);
        toast.error(err?.response?.data?.message || 'Failed to load assessment');
      } finally {
        if (!cancelled) setEditLibraryLoading(false);
      }
    };

    loadTestForEditing();
    return () => {
      cancelled = true;
    };
  }, [isEditLibrary, editLibraryTestId]);

  // Persist edits made to a library assessment: updates the library Test; server syncs
  // linked job assessments (questions and, when sent, shuffleQuestionsEnabled).
  const handleSaveLibraryEdit = useCallback(async () => {
    if (!editLibraryTestId) return;
    if (!assessmentFormData?.title?.trim()) {
      toast.error('Please enter a title.');
      return;
    }

    const isEditingMulti = isEditLibraryMultiModule || isEditLibraryRubricBased;
    if (!isEditingMulti && (!Array.isArray(questionsData) || questionsData.length === 0)) {
      toast.error('Please keep at least one question.');
      return;
    }

    setEditLibrarySaving(true);
    try {
      let payload;
      if (isEditingMulti) {
        if (!detailsDraft?.modules || detailsDraft.modules.length === 0) {
          toast.error('Please keep at least one module.');
          setEditLibrarySaving(false);
          return;
        }
        payload = {
          title: assessmentFormData.title.trim(),
          description: (assessmentFormData.description || '').trim(),
          category: assessmentFormData.category || 'General',
          difficulty: assessmentFormData.difficulty,
          estimatedTime: detailsDraft.globalDuration || detailsDraft.modules.reduce((sum, m) => sum + (m.duration || 0), 0) || 30,
          isMultiModule: isEditLibraryRubricBased ? false : true,
          isRubricBased: isEditLibraryRubricBased,
          shuffleQuestionsEnabled: questionShuffleEnabled,
          modules: detailsDraft.modules.map((m, index) => ({
            id: m.id,
            libraryTestId: m.moduleTestId || m.libraryTestId,
            order: index + 1,
            title: m.name,
            duration: m.duration,
            questionsToPick: m.questionsToPick,
            questions: m.questionBank?.map((q) => ({
              title: q.prompt || q.title || q.content,
              content: q.prompt || q.content || q.title,
              type: q.type,
              options: q.options,
              correctAnswer: q.correctAnswer || (Array.isArray(q.options) && q.options.findIndex(o => o.isCorrect) >= 0
                ? String.fromCharCode(97 + q.options.findIndex(o => o.isCorrect))
                : 'a'),
              points: q.points,
              mandatory: q.mandatory === true,
            })) || [],
          })),
        };
        // also extract flattened questions so updateLibraryAssessment handles it identically to standard tests
        payload.questions = payload.modules.flatMap(m => m.questions.map(q => ({ ...q, moduleId: m.id })));
      } else {
        payload = {
          title: assessmentFormData.title.trim(),
          description: (assessmentFormData.description || '').trim(),
          category: assessmentFormData.category || 'General',
          difficulty: assessmentFormData.difficulty,
          estimatedTime:
            assessmentDuration ||
            assessmentFormData.duration ||
            Math.max(1, questionsData.length),
          questions: questionsData.map((q) => {
            const out = {
              title: (q.title || q.content || 'Question').toString().trim(),
              content: (q.content || q.title || '').toString().trim(),
              type: q.type,
              points: Math.max(0.1, parseFloat(Number(q.points)) || 1),
            };
            if (Array.isArray(q.options) && q.options.length > 0) {
              out.options = q.options
                .filter((o) => o && typeof o.text === 'string' && o.text.trim() !== '')
                .map((o) => ({ text: o.text.trim(), isCorrect: !!o.isCorrect }));
            }
            if (q.correctAnswer != null && q.correctAnswer !== '') {
              out.correctAnswer = String(q.correctAnswer);
            }
            if (q.passageMeta) out.passageMeta = q.passageMeta;
            if (q.codingMeta) out.codingMeta = q.codingMeta;
            return out;
          }),
          shuffleQuestionsEnabled: questionShuffleEnabled,
        };
      }

      await libraryApi.updateLibraryAssessment(editLibraryTestId, payload);
      toast.success('Assessment updated');
      setDidCreate(true); // prevent close() from deleting any unrelated assessmentId
      if (typeof onClose === 'function') {
        onClose({ didEdit: true, editLibraryTestId });
      }
    } catch (err) {
      console.error('Error saving library assessment:', err);
      toast.error(err?.response?.data?.message || 'Failed to save changes');
    } finally {
      setEditLibrarySaving(false);
    }
  }, [
    editLibraryTestId,
    assessmentFormData,
    questionsData,
    assessmentDuration,
    questionShuffleEnabled,
    onClose,
    isEditLibraryMultiModule,
    isEditLibraryRubricBased,
    detailsDraft,
  ]);

  const handleSelectCreationMode = useCallback((mode) => {
    setCreationMode(mode);
    setDetailsDraft(null);
    setAdvancedConfigured(false);
    setAdvancedContinue(null);
    setIsAdvancedValid(false);
    setIsPassageReadingMode(false);
    setIsParagraphReadingMode(false);
    setIsMultiModuleMode(false);
    setIsRubricBasedMode(false);
    setCurrentStep(1);
  }, []);

  const handleSelectAssessmentCategory = useCallback((category) => {
    setAssessmentCategory(category);
    if (category === 'advanced') {
      setDetailsDraft(null);
      setCreationMode(CREATION_MODE.ADVANCED);
      setAdvancedConfigured(false);
      setAdvancedContinue(null);
      setIsAdvancedValid(false);
      setIsPassageReadingMode(false);
      setIsParagraphReadingMode(false);
      setIsMultiModuleMode(false);
      setIsRubricBasedMode(false);
      setCurrentStep(1);
    }
  }, []);

  // Clear generation context when loading finishes
  useEffect(() => {
    if (!detailsLoading) setPendingGenerationCount(null);
  }, [detailsLoading]);

  // Safe setter for validation that doesn't cause render-time updates
  const setIsFormValidSafe = useCallback((value) => {
    requestAnimationFrame(() => {
      setIsFormValid(value);
    });
  }, []);

  // Use refs to store functions to avoid render-time state updates
  const detailsSubmitRef = useRef(null);
  const setDetailsSubmitSafe = useCallback((fn) => {
    detailsSubmitRef.current = fn;
    requestAnimationFrame(() => {
      setDetailsSubmit(() => fn);
    });
  }, []);

  const setAdvancedContinueSafe = useCallback((fn) => {
    setAdvancedContinue(() => fn);
  }, []);

  const handleAdvancedConfigured = useCallback(({ formData, questions }) => {
    setAssessmentFormData(formData);
    setAssessmentDuration(formData?.duration ?? 30);
    setQuestionsData(Array.isArray(questions) ? questions : []);
    setQuestionsCreatedWithAssessment(false);
    setAdvancedConfigured(true);
  }, []);

  // Clear registered actions when changing steps (steps re-register on mount)
  useEffect(() => {
    if (currentStep !== 1) {
      setDetailsSubmit(null);
      detailsSubmitRef.current = null;
      setAdvancedContinue(null);
      setIsAdvancedValid(false);
      setAdvancedFlowStep(0);
      setAdvancedPrimaryLabel('Continue');
      advancedModalBackRef.current = () => false;
    }
    if (currentStep !== 2) {
      setBuildContinue(null);
    }
    if (currentStep !== 4) {
      setInviteFinish(null);
    }
  }, [currentStep]);

  const close = async () => {
    if (assessmentId && !didCreate) {
      try {
        logger.log('Deleting incomplete assessment:', assessmentId);
        await axios.delete(`/api/assessments/${assessmentId}`);
        logger.log('✅ Deleted incomplete assessment:', assessmentId);
      } catch (error) {
        console.error('Error deleting incomplete assessment:', error);
      }
    }

    if (typeof onClose === 'function') {
      onClose({
        didCreate: Boolean(didCreate),
        assessmentId,
        duration: assessmentDuration ?? assessmentFormData?.duration ?? 30,
      });
    }
  };

  const requestClose = () => {
    if (
      !didCreate &&
      (libraryOnly || isLibraryFlow) &&
      (assessmentFormData || (questionsData && questionsData.length > 0))
    ) {
      setShowDiscardConfirm(true);
      return;
    }
    close();
  };

  const goToStep = (step) => setCurrentStep(step);

  const isStepAccessible = (stepId) => {
    if (showCreatorSelectionStep) {
      if (stepId === -1) return true;
      if (creatorChoice !== CREATOR_CHOICE.SELF) return false;
    }
    if (showVisibilityStep) {
      if (stepId === -2) return (creatorChoice === CREATOR_CHOICE.SELF || forceShowVisibilityStep) && showVisibilityStep;
    }
    // View-only mode: only step 2 is accessible (View Questions)
    if (isViewOnly) {
      return stepId === 2;
    }
    // Edit-library mode: only step 2 is accessible (Edit Questions).
    if (isEditLibrary) {
      return stepId === 2;
    }
    // Library flow: step 1 is always accessible, step 2 and 3 require assessment selection
    if (isLibraryFlow) {
      if (stepId === 1) return true;
      if (stepId === 2) return Boolean(activeLibraryTestId) && Boolean(assessmentId);
      if (stepId === 3) return Boolean(activeLibraryTestId) && Boolean(assessmentId);
      return false;
    }
    // Library import (with libraryTestId directly): only steps 2, 3 are accessible (Edit Questions, Proctoring)
    if (isLibraryImport && !isLibraryFlow) {
      if (stepId < 2) return false;
      if (stepId === 2) return true;
      if (stepId === 3) return Boolean(assessmentId);
      if (stepId === 4) return !skipInviteStep && Boolean(assessmentId);
      return false;
    }
    if (stepId === 0) return true;
    if (!creationMode) return false;
    if (isCodingAssignmentMode) {
      if (stepId === 1) return true;
      if (stepId === 3) return Boolean(advancedConfigured);
      if (stepId === 4) return !skipInviteStep && Boolean(advancedConfigured);
      return false;
    }
    if (libraryOnly && stepId > 2) return false;
    if (skipInviteStep && stepId === 4) return false;
    if (stepId === 1) return Boolean(creationMode);
    if (stepId >= 2) {
      if (isManualMode) return Boolean(assessmentFormData);
      return Boolean(aiSections);
    }
    return false;
  };

  const getDisplaySteps = () => {
    let steps = [...baseSteps];
    // View-only mode: show only step 2 with label "View Questions"
    if (isViewOnly) {
      return [{ id: 2, label: 'View Questions' }];
    }
    // Edit-library mode: show only step 2 with label "Edit Questions"
    if (isEditLibrary) {
      return [{ id: 2, label: 'Edit Questions' }];
    }
    // Library flow: show Assessment (library selection), View Questions, Proctoring
    if (isLibraryFlow) {
      return [
        { id: 1, label: 'Select' },
        { id: 2, label: 'View Questions' },
        { id: 3, label: 'Proctoring' },
      ];
    }
    // Library import (with libraryTestId directly): skip "Choose Method" and "Details" steps, show "View Questions" instead of "Edit Questions"
    if (isLibraryImport && !isLibraryFlow) {
      steps = steps.filter(s => s.id >= 2);
      steps = steps.map(s => s.id === 2 ? { ...s, label: 'View Questions' } : s);
      if (skipInviteStep) {
        steps = steps.filter(s => s.id <= 3);
      }
      return steps;
    }
    // Hide "Choose Method" step when there's a jobPostingId (screening flow)
    if (!showChooseMethodStep) {
      steps = steps.filter(s => s.id > 0);
    } else if (isAdvancedMode) {
      steps = steps.map(s => s.id === 0 ? { ...s, label: 'Assessment Type' } : s);
    } else {
      const idx = steps.findIndex(s => s.id === 0);
      if (idx !== -1) {
        steps.splice(idx, 1,
          { id: 0, label: 'Assessment Type', _phase: 'type' },
          { id: 0, label: 'Choose Method', _phase: 'method' }
        );
      }
    }
    if (showVisibilityStep) {
      steps = [{ id: -2, label: 'Access Level' }, ...steps];
    }
    if (showCreatorSelectionStep) {
      steps = [{ id: -1, label: 'Who Creates' }, ...steps];
    }
    if ((isCodingAssignmentMode && !isPassageReadingMode) || isRubricBasedMode) {
      steps = steps.filter((s) => s.id !== 2);
    }
    if (isRubricBasedMode) {
      steps = steps.map((s) => (s.id === 1 ? { ...s, label: 'Details & Edit Questions' } : s));
    }
    if (libraryOnly) {
      steps = steps.filter(s => s.id <= 2);
    } else if (skipInviteStep) {
      steps = steps.filter(s => s.id <= 3);
    }
    return steps;
  };

  const displaySteps = getDisplaySteps();

  /** Shared + org has teams → require at least one team. Private, or no teams, or still loading teams handled here. */
  const isAccessLevelChoiceComplete = useMemo(() => {
    if (!visibilityLevel) return false;
    if (visibilityLevel === 'private') return true;
    if (loadingAccessOptions) return false;
    const deptList = accessOptions?.departments;
    if (Array.isArray(deptList) && deptList.length > 0) {
      return selectedSharedDepartmentIds.size > 0;
    }
    return true;
  }, [visibilityLevel, loadingAccessOptions, accessOptions, selectedSharedDepartmentIds]);

  const isStepComplete = (stepId) => {
    if (stepId === -2) return isAccessLevelChoiceComplete;
    if (stepId === -1) return Boolean(creatorChoice);
    // View-only mode: no steps are complete (single step view)
    if (isViewOnly) {
      return false;
    }
    // Library flow: step 1 is complete when assessment is selected
    if (isLibraryFlow) {
      if (stepId === 1) return Boolean(activeLibraryTestId) && Boolean(assessmentId);
      if (stepId === 2) return Boolean(assessmentId);
      return false;
    }
    // Library import (with libraryTestId directly): steps 0 and 1 are considered complete
    if (isLibraryImport && !isLibraryFlow) {
      if (stepId === 0 || stepId === 1) return true;
      if (stepId === 2) return Boolean(assessmentId);
      return false;
    }
    if (stepId === 0) return Boolean(creationMode);
    if (stepId === 1) {
      if (isCodingAssignmentMode) return Boolean(advancedConfigured);
      if (creationMode === 'manual') return Boolean(assessmentFormData);
      return Boolean(aiSections);
    }
    if (stepId === 2) return Boolean(assessmentId);
    return false;
  };

  const handleGeneratedSections = useCallback((sections) => {
    logger.log('📋 AI sections received in modal:', sections);
    setAiSections(sections);
  }, []);

  const syncShuffleState = useCallback(
    (value) => {
      const next = Boolean(value);
      setQuestionShuffleEnabled(next);
      setAssessmentFormData((prev) => (prev ? { ...prev, shuffleQuestionsEnabled: next } : prev));
    },
    [setAssessmentFormData]
  );

  useEffect(() => {
    if (typeof assessmentFormData?.shuffleQuestionsEnabled === 'boolean') {
      setQuestionShuffleEnabled(assessmentFormData.shuffleQuestionsEnabled);
    }
  }, [assessmentFormData?.shuffleQuestionsEnabled]);

  const handleRegenerateQuestions = useCallback(async () => {
    setRegeneratingQuestions(true);
    try {
      const numberOfQuestions = Math.min(
        50,
        Math.max(1, parseInt(questionsData?.length, 10) || pendingGenerationCount || 10)
      );
      const duration = Math.min(40, Math.max(5, parseInt(assessmentDuration, 10) || numberOfQuestions));

      const requestData = {
        constraints: {
          title: assessmentFormData?.title?.trim() || 'Assessment',
          category: assessmentFormData?.category || undefined,
          difficulty: assessmentFormData?.difficulty || undefined,
          assessmentType: Array.isArray(assessmentFormData?.assessmentTypes)
            ? assessmentFormData.assessmentTypes.join(', ')
            : undefined,
          questionTypes: Array.isArray(assessmentFormData?.questionTypes)
            ? assessmentFormData.questionTypes
            : [],
          categories: Array.isArray(assessmentFormData?.assessmentTypes)
            ? assessmentFormData.assessmentTypes
            : [],
          additionalContext: assessmentFormData?.additionalContext || '',
          duration,
          numberOfQuestions,
        },
      };

      const linkedJobId = jobPostingId || assessmentFormData?.jobPosting;
      if (linkedJobId) {
        requestData.jobPostingId = linkedJobId;
      }

      const recommendationsResponse = await axios.post(
        '/api/assessments/recommendations',
        requestData
      );
      const sections = recommendationsResponse.data?.recommendations?.sections || [];
      if (!Array.isArray(sections) || sections.length === 0) {
        toast.error('Could not regenerate questions. Please try again.');
        return;
      }

      const flatQuestions = [];
      sections.forEach((section) => {
        if (!section?.questions?.length) return;
        section.questions.forEach((q) => {
          const type = normalizeQuestionType(q.type);
          const prompt = (q.prompt || '').trim() || 'Question';
          const customQuestion = {
            title: prompt,
            content: prompt,
            type,
            points: 1,
            skills: Array.isArray(q.skills) ? q.skills : [],
          };
          if (type === 'multiple-choice' && Array.isArray(q.options) && q.options.length >= 2) {
            customQuestion.options = q.options
              .filter((opt) => opt && typeof opt.text === 'string')
              .map((opt) => ({ text: opt.text.trim(), isCorrect: !!opt.isCorrect }));
            const correctIdx = customQuestion.options.findIndex((o) => o.isCorrect);
            customQuestion.correctAnswer = correctIdx >= 0 ? String.fromCharCode(97 + correctIdx) : 'a';
          } else if (type === 'true-false') {
            customQuestion.options = [
              { text: 'True', isCorrect: q.correctAnswer !== 'b' },
              { text: 'False', isCorrect: q.correctAnswer === 'b' },
            ];
            customQuestion.correctAnswer = q.correctAnswer === 'b' ? 'b' : 'a';
          } else {
            customQuestion.correctAnswer = Array.isArray(q.correctAnswer)
              ? q.correctAnswer.join(',').trim()
              : (q.correctAnswer || '').toString().trim();
          }
          if (type === 'passage-reading' && q.passageMeta) {
            customQuestion.passageMeta = q.passageMeta;
          }
          flatQuestions.push(customQuestion);
        });
      });

      if (assessmentId) {
        await axios.put(`/api/assessments/${assessmentId}/custom-questions`, {
          questions: flatQuestions.map((question, idx) => ({
            title: question.title,
            content: question.content,
            type: question.type,
            options: Array.isArray(question.options) ? question.options : [],
            correctAnswer: question.correctAnswer || '',
            points: Math.max(0.1, parseFloat(Number(question.points)) || 1),
            order: idx + 1,
            codingMeta: question.codingMeta || null,
            passageMeta: question.passageMeta || null,
          })),
        });

        const refreshResponse = await axios.get(`/api/assessments/${assessmentId}`);
        const savedQuestions = refreshResponse.data?.assessment?.content?.customQuestions || [];
        setQuestionsData(savedQuestions.length > 0 ? savedQuestions : flatQuestions);
      } else {
        setQuestionsData(flatQuestions);
      }

      setAiSections(sections);
      setBuildRefreshKey((prev) => prev + 1);
      toast.success('Questions regenerated');
    } catch (error) {
      console.error('Regenerate questions failed:', error);
      toast.error('Failed to regenerate questions');
    } finally {
      setRegeneratingQuestions(false);
    }
  }, [
    assessmentFormData,
    creationMode,
    isLibraryImport,
    isLibraryFlow,
    questionsData?.length,
    pendingGenerationCount,
    assessmentDuration,
    jobPostingId,
    assessmentId,
  ]);

  const handleCreated = useCallback(
    async (data) => {
      logger.log('✅ Form data and AI sections received in modal:', data);
      const formData = data.formData || data;
      setAssessmentFormData(formData);
      setAssessmentDuration(formData?.duration ?? null);
      const sections = data.aiSections || null;
      setAiSections(sections);

      if ((!sections || !Array.isArray(sections) || sections.length === 0) && !formData.isMultiModule) {
        logger.log('⚠️ No sections to save, moving to step 2');
        setTimeout(() => setCurrentStep(2), 300);
        return;
      }

      const flatQuestions = [];
      if (sections && Array.isArray(sections)) {
        sections.forEach((section) => {
          if (!section?.questions?.length) return;
          section.questions.forEach((q) => {
            const type = normalizeQuestionType(q.type);
            const prompt = (q.prompt || '').trim() || 'Question';
            const customQuestion = {
              title: prompt,
              content: prompt,
              type,
              points: 1,
              skills: Array.isArray(q.skills) ? q.skills : [],
            };
            if (type === 'multiple-choice' && Array.isArray(q.options) && q.options.length >= 2) {
              customQuestion.options = q.options
                .filter((opt) => opt && typeof opt.text === 'string')
                .map((opt) => ({ text: opt.text.trim(), isCorrect: !!opt.isCorrect }));
              const correctIdx = customQuestion.options.findIndex((o) => o.isCorrect);
              customQuestion.correctAnswer =
                correctIdx >= 0 ? String.fromCharCode(97 + correctIdx) : 'a';
            } else if (type === 'true-false') {
              customQuestion.options = [
                { text: 'True', isCorrect: q.correctAnswer !== 'b' },
                { text: 'False', isCorrect: q.correctAnswer === 'b' },
              ];
              customQuestion.correctAnswer = q.correctAnswer === 'b' ? 'b' : 'a';
            } else {
              customQuestion.correctAnswer = Array.isArray(q.correctAnswer)
                ? q.correctAnswer.join(',').trim()
                : (q.correctAnswer || '').toString().trim();
            }
            if (type === 'passage-reading' && q.passageMeta) {
              customQuestion.passageMeta = q.passageMeta;
            }
            flatQuestions.push(customQuestion);
          });
        });
      }

      const questionType =
        formData.assessmentTypes?.length > 0 ? formData.assessmentTypes[0] : 'Technical';
      const duration = formData?.duration ?? 30;

      try {
        const createPayload = {
          title: formData.title || 'Assessment',
          jobPosting: formData.jobPosting || jobPostingId,
          questionType,
          noOfQuestions: flatQuestions.length,
          assessmentDuration: duration,
          duration,
          passingScore: formData.passingScore ?? 70,
          customQuestions: flatQuestions,
          shuffleQuestionsEnabled: questionShuffleEnabled,
          isMultiModule: Boolean(formData.isMultiModule),
          modules: formData.modules || [],
        };
        logger.log('📤 Creating assessment with payload:', createPayload);
        const createRes = await axios.post('/api/assessments', createPayload);
        const newId = createRes.data?.assessment?._id;
        if (newId) {
          setAssessmentId(newId);
          setQuestionsData(flatQuestions);
          // For AI-generated assessments we persist questions at creation time
          setQuestionsCreatedWithAssessment(true);
          logger.log('✅ Assessment created in DB:', newId);
        }
      } catch (err) {
        console.error('Error creating assessment after recommendations:', err);
        toast.error(
          err.response?.data?.message ||
          'Failed to save assessment. You can still continue to edit.'
        );
      }
      setTimeout(() => setCurrentStep(2), 300);
    },
    [jobPostingId]
  );

  const handleFinishAndPublish = useCallback(async (snapshot) => {
    const formDataForCreate = snapshot?.formData ?? assessmentFormData;
    const questionsForCreate = snapshot?.questions ?? questionsData;
    const durationStateForCreate =
      snapshot?.formData?.assessmentDuration ??
      snapshot?.formData?.duration ??
      snapshot?.formData?.globalDuration ??
      snapshot?.formData?.estimatedTime ??
      (snapshot && 'assessmentDuration' in snapshot
        ? snapshot.assessmentDuration
        : assessmentDuration);

    let newAssessmentId = null;
    let moduleMap = {};
    setFinishing(true);
    try {
      if (!formDataForCreate && !isLibraryImport && !isLibraryFlow) {
        toast.error('Missing assessment data. Please go back to Step 1.');
        setFinishing(false);
        return;
      }
      const questionCount = questionsForCreate?.length || 0;
      const computedDuration = questionCount === 0 ? 30 : Math.max(5, questionCount);
      const duration =
        durationStateForCreate != null && Number(durationStateForCreate) >= 1
          ? Number(durationStateForCreate)
          : computedDuration;

      // If we already have an assessment (e.g., from library import), use it instead of creating a new one
      if (assessmentId) {
        newAssessmentId = assessmentId;
        logger.log('📚 Using existing assessment from library import:', newAssessmentId);
        if (Object.keys(moduleMapState).length > 0) {
          // Restore mapping from state on retry
          moduleMap = { ...moduleMapState };
        }
      } else {
        const assessmentData = {
          title: formDataForCreate.title,
          jobPosting: formDataForCreate.jobPosting || jobPostingId,
          role: formDataForCreate.role || 'IT',
          category: formDataForCreate.category || '',
          duration,
          assessmentDuration: duration,
          passingScore: formDataForCreate.passingScore || 70,
          enableFullScreenMonitoring: proctoringState?.enableFullScreenMonitoring || false,
          fullScreenToleranceLimit: proctoringState?.fullScreenToleranceLimit || 3,
          enableTabSwitchMonitoring: proctoringState?.enableTabSwitchMonitoring || false,
          tabSwitchToleranceLimit: proctoringState?.tabSwitchToleranceLimit || 2,
          enableCopyPasteBlocking: proctoringState?.enableCopyPasteBlocking || false,
          copyPasteToleranceLimit: proctoringState?.copyPasteToleranceLimit || 1,
          enableMouseMonitoring: proctoringState?.enableMouseMonitoring || false,
          mouseMonitoringToleranceLimit: proctoringState?.mouseMonitoringToleranceLimit || 2,
          shuffleQuestionsEnabled: questionShuffleEnabled,
          // Multi-module fields — must be persisted before publish so the backend
          // publish validation can correctly skip the "0 questions" check.
          isMultiModule: formDataForCreate.isMultiModule || false,
          isRubricBased: formDataForCreate.isRubricBased || false,
          modules: formDataForCreate.modules || [],
          // 'personality-ipip50': the server adds the 50 fixed statements itself.
          ...(formDataForCreate.assessmentKind ? { assessmentKind: formDataForCreate.assessmentKind } : {}),
        };
        const createResponse = await axios.post('/api/assessments', assessmentData);
        newAssessmentId = createResponse.data.assessment._id;
        
        // Map local module IDs to real backend ObjectIds
        const savedModules = createResponse.data.assessment.modules || [];
        const localModules = formDataForCreate.modules || [];
        if (localModules.length === savedModules.length) {
          localModules.forEach((mod, index) => {
            if (mod.id && savedModules[index]._id) {
              moduleMap[mod.id] = savedModules[index]._id;
            }
          });
          setModuleMapState(prev => ({ ...prev, ...moduleMap }));
        }
        
        setAssessmentId(newAssessmentId);
      }

      // For library imports, library flow, or flows where questions were already saved with the assessment
      // (e.g. AI-generated assessments), skip adding them again to avoid duplicates.
      const shouldAddQuestions =
        !isLibraryImport &&
        !isLibraryFlow &&
        !questionsCreatedWithAssessment &&
        questionsForCreate &&
        questionsForCreate.length > 0;

      if (shouldAddQuestions) {
        if (isAdvancedMode) {
          await axios.put(`/api/assessments/${newAssessmentId}/custom-questions`, {
            questions: (questionsForCreate || []).map((question, idx) => {
              let mappedOptions = Array.isArray(question.options) ? question.options : [];
              let mappedCorrectAnswer = Array.isArray(question.correctAnswer)
                ? question.correctAnswer.join(',').trim()
                : (question.correctAnswer || '').toString().trim();

              // If options are raw strings (from AI parsing), convert them to { text, isCorrect } objects
              if (mappedOptions.length > 0 && typeof mappedOptions[0] === 'string') {
                const correctText = mappedCorrectAnswer.toLowerCase();
                mappedOptions = mappedOptions.map((opt) => ({
                  text: opt,
                  isCorrect: String(opt).toLowerCase().trim() === correctText
                }));
                const correctIdx = mappedOptions.findIndex(o => o.isCorrect);
                if (correctIdx >= 0) {
                  mappedCorrectAnswer = String.fromCharCode(97 + correctIdx);
                } else {
                  mappedCorrectAnswer = 'a';
                }
              }

              const getValidModuleId = (id) => {
                if (moduleMap[id]) return moduleMap[id];
                if (/^[0-9a-fA-F]{24}$/.test(id)) return id;
                return null;
              };

              return {
                _id: question._id,
                title: (question.title || question.content || 'Question').trim(),
                content: (question.content || question.title || 'Question').trim(),
                type: question.type || 'code',
                options: mappedOptions,
                correctAnswer: mappedCorrectAnswer,
                points: Math.max(0.1, parseFloat(Number(question.points)) || 1),
                order: idx + 1,
                codingMeta: question.codingMeta || null,
                passageMeta: question.passageMeta || null,
                moduleId: getValidModuleId(question.moduleId),
              };
            }),
          });
        } else {
          const payloadQuestions = (questionsForCreate || []).map((question, idx) => {
            const rawTitle = (question.title || question.content || '').trim();
            const rawContent = (question.content || question.title || '').trim();
            const normalizedType = normalizeQuestionType(question.type);
            const titleText = rawTitle.length > 0 ? rawTitle : 'Question';
            const contentText =
              rawContent.length > 0 ? rawContent : rawTitle.length > 0 ? rawTitle : 'Question';

            const getValidModuleId = (id) => {
              if (moduleMap[id]) return moduleMap[id];
              if (/^[0-9a-fA-F]{24}$/.test(id)) return id;
              return null;
            };

            const questionData = {
              _id: question._id,
              title: titleText.length > 200 ? titleText.substring(0, 200) : titleText,
              content: contentText,
              type: normalizedType,
              points: Math.max(0.1, parseFloat(Number(question.points)) || 1),
              order: idx + 1,
              moduleId: getValidModuleId(question.moduleId),
            };

            if (normalizedType === 'multiple-choice' && Array.isArray(question.options)) {
              const validOptions = question.options.filter(
                (opt) => opt && opt.text && opt.text.trim() !== ''
              );
              if (validOptions.length < 2) {
                throw new Error(
                  `Question "${questionData.title}" must have at least 2 valid options`
                );
              }
              const correctOptions = validOptions.filter((opt) => opt.isCorrect);
              if (correctOptions.length === 0) {
                throw new Error(
                  `Question "${questionData.title}" must have at least one correct option`
                );
              }
              questionData.options = validOptions;
              const correctIndices = validOptions
                .map((opt, idx) => (opt.isCorrect ? String.fromCharCode(97 + idx) : null))
                .filter(Boolean);
              questionData.correctAnswer =
                correctIndices.length > 0 ? correctIndices.join(',') : 'a';
            } else if (normalizedType === 'true-false') {
              questionData.options = [
                { text: 'True', isCorrect: question.correctAnswer === 'a' },
                { text: 'False', isCorrect: question.correctAnswer === 'b' },
              ];
              questionData.correctAnswer = question.correctAnswer === 'b' ? 'b' : 'a';
            } else {
              questionData.correctAnswer = Array.isArray(question.correctAnswer)
                ? question.correctAnswer.join(',').trim()
                : (question.correctAnswer || '').toString().trim();
            }

            if (normalizedType === 'passage-reading' && question.passageMeta) {
              questionData.passageMeta = question.passageMeta;
            }

            return questionData;
          });

          await axios.put(`/api/assessments/${newAssessmentId}/custom-questions`, {
            questions: payloadQuestions,
          });
        }
      } else if (
        !isLibraryImport &&
        !isLibraryFlow &&
        !formDataForCreate?.isMultiModule &&
        // Personality: the server already added the 50 fixed statements.
        !formDataForCreate?.assessmentKind &&
        (!questionsForCreate || questionsForCreate.length === 0)
      ) {
        toast.error('No questions to save. Please go back to Step 2 and generate questions.');
        try {
          await axios.delete(`/api/assessments/${newAssessmentId}`);
        } catch (deleteErr) {
          console.error('Error deleting assessment after question save failure:', deleteErr);
        }
        setFinishing(false);
        return;
      }

      if (proctoringState && newAssessmentId) {
        try {
          await axios.post(`/api/proctoring/config/${newAssessmentId}`, {
            fullScreen: {
              enabled: proctoringState.enableFullScreenMonitoring || false,
              forceFullScreen: proctoringState.enableFullScreenMonitoring || false,
              toleranceLimit: proctoringState.fullScreenToleranceLimit || 3,
            },
            tabProctoring: {
              enabled: proctoringState.enableTabSwitchMonitoring || false,
              toleranceLimit: proctoringState.tabSwitchToleranceLimit || 2,
              blockTabSwitch: proctoringState.enableTabSwitchMonitoring || false,
            },
            copyPastePrevention: {
              enabled: proctoringState.enableCopyPasteBlocking || false,
              toleranceLimit: proctoringState.copyPasteToleranceLimit || 1,
            },
            mouseMonitoring: {
              enabled: proctoringState.enableMouseMonitoring || false,
              toleranceLimit: proctoringState.mouseMonitoringToleranceLimit || 2,
            },
            cameraMonitoring: {
              enabled: proctoringState.enableCameraMonitoring || false,
              requireCamera: proctoringState.enableCameraMonitoring || false,
              recordVideo: proctoringState.enableCameraMonitoring || false,
            },
          });
        } catch (proctoringErr) {
          console.error('Error saving proctoring config:', proctoringErr);
        }
      }

      // In library-only advanced flow, auto-save created coding/paragraph/multi-module
      // assessment to library so it appears in Assessments Library list immediately.
      const shouldAutoSaveToLibrary =
        libraryOnly &&
        isAdvancedMode &&
        formDataForCreate?.title &&
        (questionsForCreate?.length || formDataForCreate?.isMultiModule || formDataForCreate?.assessmentKind);

      if (shouldAutoSaveToLibrary) {
        try {
          const durationToSave =
            durationStateForCreate ??
            formDataForCreate?.duration ??
            (formDataForCreate?.isMultiModule
              ? (formDataForCreate.modules || []).reduce((sum, m) => sum + (m.duration || 0), 0)
              : Math.max(5, questionsForCreate?.length || 0));
          const libraryPayload = {
            title: formDataForCreate.title.trim(),
            description: formDataForCreate.additionalContext || '',
            role: formDataForCreate.role || 'IT',
            difficulty:
              formDataForCreate.difficulty === 'beginner'
                ? 'Easy'
                : formDataForCreate.difficulty === 'intermediate'
                  ? 'Medium'
                  : formDataForCreate.difficulty === 'advanced' ||
                    formDataForCreate.difficulty === 'expert'
                    ? 'Hard'
                    : 'Medium',
            estimatedTime: durationToSave,
            category: formDataForCreate.isMultiModule ? 'Advanced' : (formDataForCreate.category || 'General'),
            questions: (questionsForCreate || []).map((q) => ({
              title: q.title || q.content,
              content: q.content || q.title,
              type: q.type,
              options: q.options,
              correctAnswer: q.correctAnswer || (Array.isArray(q.options) && q.options.findIndex(o => o.isCorrect) >= 0 ? String.fromCharCode(97 + q.options.findIndex(o => o.isCorrect)) : 'a'),
              points: q.points ?? 1,
              codingMeta: q.codingMeta || null,
              passageMeta: q.passageMeta || null,
              moduleId: q.moduleId || null,
            })),
            assessmentId: newAssessmentId || null,
            shuffleQuestionsEnabled: questionShuffleEnabled,
            // Multi-module specific fields
            isMultiModule: formDataForCreate.isMultiModule || false,
            isRubricBased: formDataForCreate.isRubricBased || false,
            modules: formDataForCreate.modules || [],
            // Personality: the server stores its own copy of the 50 fixed statements.
            ...(formDataForCreate.assessmentKind ? { assessmentKind: formDataForCreate.assessmentKind } : {}),
          };
          await axios.post('/api/assessments/save-to-library', libraryPayload);
          setHasSavedToLibrary(true);
        } catch (saveLibErr) {
          console.error('Auto save to library failed for advanced assessment:', saveLibErr);
          toast.error('Failed to save to Library. Please try again.');
          setFinishing(false);
          return;
        }
      }

      try {
        await axios.post(`/api/assessments/${newAssessmentId}/publish`);
      } catch (publishError) {
        toast.error(publishError.response?.data?.message || 'Failed to publish assessment');
        setFinishing(false);
        return;
      }
      setDidCreate(true);
      if (typeof onClose === 'function') {
        await onClose({
          didCreate: true,
          assessmentId: newAssessmentId,
          duration: durationStateForCreate ?? formDataForCreate?.duration ?? 30,
        });
      }
    } catch (error) {
      console.error('Error finishing assessment:', error);
      toast.error(error.response?.data?.message || 'Failed to complete assessment');
      if (newAssessmentId && !didCreate) {
        try {
          await axios.delete(`/api/assessments/${newAssessmentId}`);
        } catch (deleteErr) {
          console.error('Error deleting assessment after failure:', deleteErr);
        } finally {
          setAssessmentId(null);
        }
      }
    } finally {
      setFinishing(false);
      setDetailsLoading(false);
    }
  }, [
    assessmentFormData,
    questionsData,
    questionsCreatedWithAssessment,
    proctoringState,
    assessmentDuration,
    jobPostingId,
    onClose,
    didCreate,
    isLibraryImport,
    isLibraryFlow,
    isAdvancedMode,
    assessmentId,
    libraryOnly,
  ]);

  const handleSaveToLibrary = useCallback(async () => {
    const isAdvanced = assessmentCategory === 'advanced';
    if (!assessmentFormData?.title || (!questionsData?.length && !isAdvanced)) {
      toast.error('Add a title and at least one question before saving to library.');
      return;
    }
    setSavingToLibrary(true);
    try {
      const duration = assessmentDuration ?? assessmentFormData?.duration ?? Math.max(5, questionsData?.length || 0);
      const payload = {
        title: assessmentFormData.title.trim(),
        description: assessmentFormData.additionalContext || '',
        role: assessmentFormData.role || 'IT',
        difficulty: assessmentFormData.difficulty === 'beginner' ? 'Easy' : assessmentFormData.difficulty === 'intermediate' ? 'Medium' : assessmentFormData.difficulty === 'advanced' || assessmentFormData.difficulty === 'expert' ? 'Hard' : 'Medium',
        estimatedTime: duration,
        category: isAdvanced ? 'Advanced' : (assessmentFormData.category || 'General'),
        questions: (questionsData || []).map((q) => ({
          title: q.title || q.content,
          content: q.content || q.title,
          type: q.type,
          options: q.options,
          correctAnswer: q.correctAnswer || (Array.isArray(q.options) && q.options.findIndex(o => o.isCorrect) >= 0 ? String.fromCharCode(97 + q.options.findIndex(o => o.isCorrect)) : 'a'),
          points: q.points ?? 1,
          passageMeta: q.passageMeta || null,
        })),
        assessmentId: assessmentId || null,
        visibility: visibilityLevel,
        sharedWithDepartmentIds: visibilityLevel === 'shared' ? Array.from(selectedSharedDepartmentIds) : [],
        shuffleQuestionsEnabled: questionShuffleEnabled,
        // Multi-module specific fields
        isMultiModule: assessmentFormData?.isMultiModule || false,
        isRubricBased: assessmentFormData?.isRubricBased || false,
        modules: assessmentFormData?.modules || [],
      };
      await axios.post('/api/assessments/save-to-library', payload);
      setShowSaveToLibraryConfirm(false);
      setHasSavedToLibrary(true);
      toast.success('Assessment saved to library. You can find it under Library → Assessments Library.');
      if (libraryOnly && typeof onClose === 'function') {
        onClose({ didCreate: false, savedToLibrary: true });
      } else {
        // Continue to next step after saving
        setCurrentStep(3);
      }
    } catch (err) {
      console.error('Save to library error:', err);
      toast.error(err.response?.data?.message || 'Failed to save assessment to library.');
    } finally {
      setSavingToLibrary(false);
    }
  }, [assessmentFormData, questionsData, assessmentDuration, libraryOnly, onClose, visibilityLevel, selectedSharedDepartmentIds]);

  return (
    <div className="flex flex-col h-full w-full prevent-body-scroll" style={{ minHeight: 0, maxHeight: '100%' }}>
      {/* Top header: for Assessment Details (step 1, non-library) title + subtitle live in the right column only — matches Interview Details UX */}
      {(libraryOnly || !(currentStep === 1 && !isLibraryFlow)) && (
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex-1 min-w-0">
            <h1 data-testid="assessment-modal-title" className="text-lg font-semibold text-gray-900">
              {currentStep === -1 && 'Who will create this assessment?'}
              {currentStep === -2 && showVisibilityStep && 'Configure access level'}
              {currentStep === -3 && 'Send an external creation link'}
              {currentStep === 0 && (step0Phase === 'type' ? 'Choose Assessment Type' : 'Choose Creation Method')}
              {currentStep === 1 && 'Select Assessment'}
              {currentStep === 2 && (viewOnlyLoading ? 'Loading Assessment...' : isViewOnly && isViewOnlyMultiModule ? 'View Modules' : isViewOnly ? 'View Questions' : editLibraryLoading ? 'Loading Assessment...' : isEditLibrary && isEditLibraryMultiModule ? 'Edit Modules' : isEditLibrary ? 'Edit Assessment' : libraryImportLoading ? 'Importing Assessment...' : isLibraryImportMultiModule ? 'View Modules' : isLibraryImport ? 'View Questions' : isManualMode ? 'Create Questions' : 'Preview & Edit Questions')}
              {currentStep === 3 && 'Proctoring Settings'}
              {currentStep === 4 && 'Invite Candidates'}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {currentStep === -2 && showVisibilityStep &&
                'Select whether this library assessment should be shared with your team or kept private.'}
              {currentStep === -1 &&
                'Choose whether you want to create the assessment yourself, or generate a link to let someone else create it for you.'}
              {currentStep === -3 &&
                'Generate and share this link. The recipient can open it and create the assessment in their browser.'}  
              {currentStep === 0 && (step0Phase === 'type'
                ? 'Select the type of assessment you want to create.'
                : 'Select how you want to create your assessment — with AI assistance or manually from scratch.')}
              {currentStep === 1 &&
                'Choose an assessment from your library to use for this screening.'}
              {currentStep === 2 && (viewOnlyLoading
                ? 'Please wait while we load the assessment...'
                : isViewOnly
                  ? 'View the questions in this assessment. This is a read-only preview.'
                  : editLibraryLoading
                    ? 'Please wait while we load the assessment...'
                    : isEditLibrary
                      ? 'Edit the library template. Changes apply to future screenings only — existing screenings keep their original questions.'
                      : libraryImportLoading
                        ? 'Please wait while we import your assessment from the library...'
                        : isLibraryImport
                          ? 'Review the imported questions from your library assessment. Edit, reorder, or add new questions as needed.'
                          : isManualMode
                            ? 'Add, edit, and organize your questions manually. Click "Add Question" to get started.'
                            : 'Review, reorder, and edit your questions. Click a question to expand and see options.')}
              {currentStep === 3 &&
                'Choose monitoring and security rules to protect the integrity of this assessment.'}
              {currentStep === 4 &&
                'Share a link or send invites so candidates can start taking the assessment.'}
            </p>
          </div>
          {(libraryOnly || isEditLibrary) && (
            <button
              type="button"
              onClick={requestClose}
              className="ml-4 inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Close"
              data-testid="close-modal-btn"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {/* Main Body with Sidebar and Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        {!isExternalLinkStep && (
          <div className="w-52 flex-shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col">
            {/* <div className="p-4 border-b border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Steps</p>
            {jobPostingId && (
              <p className="mt-1 text-xs text-gray-400 truncate" title={jobPostingId}>
                Linked to job posting
              </p>
            )}
            {libraryOnly && (
              <p className="mt-1 text-xs text-gray-400">
                Save to library
              </p>
            )}
          </div> */}

            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {displaySteps.map((step, index) => {
                const isPhaseStep = Boolean(step._phase);
                const isActive = isPhaseStep
                  ? currentStep === step.id && step0Phase === step._phase
                  : currentStep === step.id && !(step.id === 0 && showChooseMethodStep && !isAdvancedMode);
                const complete = isPhaseStep
                  ? step._phase === 'type' ? Boolean(assessmentCategory) : Boolean(creationMode)
                  : isStepComplete(step.id);
                const accessible = isPhaseStep
                  ? step._phase === 'type' ? isStepAccessible(step.id) : assessmentCategory === 'standard'
                  : isStepAccessible(step.id);
                const stepNumber = index + 1;

                const handleStepClick = () => {
                  if (!accessible) return;
                  if (step._phase === 'type') {
                    setDetailsDraft(null);
                    setAssessmentCategory(null);
                    setCreationMode(null);
                    setIsPassageReadingMode(false);
                    setIsParagraphReadingMode(false);
                    setIsMultiModuleMode(false);
                    setIsRubricBasedMode(false);
                    goToStep(0);
                  } else if (step._phase === 'method') {
                    setDetailsDraft(null);
                    setAssessmentCategory('standard');
                    setCreationMode(null);
                    setIsPassageReadingMode(false);
                    setIsParagraphReadingMode(false);
                    setIsMultiModuleMode(false);
                    setIsRubricBasedMode(false);
                    goToStep(0);
                  } else {
                    if (step.id === 0) {
                      setIsPassageReadingMode(false);
                      setIsParagraphReadingMode(false);
                      setIsMultiModuleMode(false);
                      setIsRubricBasedMode(false);
                    }
                    goToStep(step.id);
                  }
                };

                return (
                  <button
                    key={`${step.id}-${step._phase || ''}`}
                    type="button"
                    onClick={handleStepClick}
                    disabled={!accessible}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${isActive
                        ? 'bg-[#007BFF]/10 text-[#007BFF]'
                        : complete
                          ? 'text-gray-700 hover:bg-gray-100'
                          : accessible
                            ? 'text-gray-600 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    <div
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${isActive
                          ? 'bg-[#007BFF] text-white'
                          : complete
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                    >
                      {complete && !isActive ? (
                        <CheckIcon className="w-3.5 h-3.5" />
                      ) : (
                        <span>{stepNumber}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm font-medium truncate ${isActive ? 'text-[#007BFF]' : 'text-gray-700'
                          }`}
                      >
                        {step.label}
                      </p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden relative">
          {currentStep === 1 && !isLibraryFlow && (!isAdvancedMode || isPassageReadingMode) && (
            <div className="flex h-12 shrink-0 items-center justify-between border-b border-gray-200 px-6" data-testid="assessment-creator-header">
              <h2 className="text-lg font-semibold text-gray-900" data-testid="assessment-creator-heading">
                {isViewOnly ? 'View Assessment' : isEditLibrary ? 'Edit Assessment Questions' : 'Assessment Creator'}
              </h2>
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-6 pt-4 pb-20 min-h-0">
            {currentStep === -1 && showCreatorSelectionStep && (
              <div className="h-full flex flex-col">
                <div className="flex flex-col items-center justify-center py-8 px-4 h-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl w-full items-stretch">
                    <button
                      type="button"
                      data-testid="self-create-btn"
                      onClick={() => {
                        setCreatorChoice(CREATOR_CHOICE.SELF);
                        setCurrentStep(showVisibilityStep ? -2 : 0);
                      }}
                      className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
                    >
                      <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#007BFF]/10 to-[#007BFF]/5 mb-4 group-hover:from-[#007BFF]/20 group-hover:to-[#007BFF]/10 transition-colors">
                        <ClipboardDocumentIcon className="w-7 h-7 text-[#007BFF]" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">I&apos;ll create it</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Continue to choose AI vs manual creation and build the assessment here.
                      </p>
                      <ul className="space-y-2.5 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>Create now inside TalentCrux</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>Use AI generation or build from scratch</span>
                        </li>
                      </ul>
                    </button>

                    <button
                      type="button"
                      data-testid="send-link-to-someone-btn"
                      onClick={() => {
                        setCreatorChoice(CREATOR_CHOICE.EXTERNAL);
                        setCurrentStep(-3);
                      }}
                      className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
                    >
                      <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gray-100 mb-4 group-hover:bg-[#007BFF]/10 transition-colors">
                        <LinkIcon className="w-7 h-7 text-gray-600 group-hover:text-[#007BFF]" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Send a link to someone</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Generate a secure link so another person can create the assessment for you.
                      </p>
                      <ul className="space-y-2.5 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>They can complete creation in a separate tab</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>You can share via email/Slack etc.</span>
                        </li>
                      </ul>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === -3 && showCreatorSelectionStep && creatorChoice === CREATOR_CHOICE.EXTERNAL && (
              <div className="h-full flex flex-col items-center justify-center py-10 px-4">
                <div className="w-full max-w-2xl">
                  <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-100">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="inline-flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#007BFF]/10">
                              <LinkIcon className="w-5 h-5 text-[#007BFF]" />
                            </span>
                            <h3 className="text-lg font-semibold text-gray-900">External Creation Link</h3>
                          </div>
                          <p className="text-sm text-gray-600">
                            Share this link with your teammate. They can open it and create the assessment for you.
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Tip: share the link privately (direct message or email).
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setCreatorChoice(null);
                            setExternalCreationLink('');
                            setCurrentStep(-1);
                          }}
                          className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                        >
                          Back
                        </button>
                      </div>
                    </div>

                    <div className="p-6 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <button
                          type="button"
                          data-testid="generate-link-btn"
                          onClick={handleGenerateExternalCreationLink}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] transition-colors"
                        >
                          <LinkIcon className="w-4 h-4" />
                          Generate link
                        </button>
                        <div className="text-xs text-gray-500">
                          {externalCreationLink ? 'Link ready to share.' : 'Generate a link to continue.'}
                        </div>
                      </div>

                      {externalCreationLink && (
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                          <label className="block text-xs font-semibold text-gray-700 mb-2">
                            Shareable URL
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={externalCreationLink}
                              className="flex-1 min-w-0 px-3 py-2.5 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                if (!externalCreationLink) return;
                                try {
                                  if (navigator?.clipboard?.writeText) {
                                    await navigator.clipboard.writeText(externalCreationLink);
                                  }
                                  setExternalLinkCopied(true);
                                  setTimeout(() => setExternalLinkCopied(false), 2000);
                                  toast.success('Link copied.');
                                } catch (err) {
                                  toast.error('Could not copy link. Please copy manually.');
                                }
                              }}
                              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-800 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                            >
                              <ClipboardDocumentIcon className="w-5 h-5" />
                              {externalLinkCopied ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === -2 && showVisibilityStep && (
              <div className="h-full flex flex-col items-center justify-center py-10 px-4">
                <div className="w-full max-w-2xl">
                  <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50/80">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid="access-level-heading">Configure access level</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Configure who can see this library assessment.
                      </p>
                    </div>
                    <div className="p-6 space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Access Level (required)
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label
                            data-testid="access-level-shared"
                            className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              visibilityLevel === 'shared'
                                ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="visibilityLevel"
                              value="shared"
                              checked={visibilityLevel === 'shared'}
                              onChange={() => setVisibilityLevel('shared')}
                              className="mt-1 w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <UserGroupIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                                <span className="font-medium text-gray-900">Shared</span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                Select which teams can see this assessment
                              </p>
                            </div>
                          </label>

                          <label
                            data-testid="access-level-private"
                            className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              visibilityLevel === 'private'
                                ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="visibilityLevel"
                              value="private"
                              checked={visibilityLevel === 'private'}
                              onChange={() => setVisibilityLevel('private')}
                              className="mt-1 w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <LockClosedIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                                <span className="font-medium text-gray-900">Private</span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">Only you can see this assessment</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {visibilityLevel === 'shared' && (
                        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                          <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                            <h4 className="text-sm font-semibold text-gray-900">Shared with teams</h4>
                            <p className="text-xs text-gray-500 mt-1">
                              Select one or more teams. Only those teams can see this assessment in the library.
                            </p>
                          </div>
                          <div className="p-4 max-h-[260px] overflow-y-auto">
                            {loadingAccessOptions || !Array.isArray(accessOptions?.departments) ? (
                              <div className="flex flex-col items-center justify-center py-10 gap-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#007BFF] border-t-transparent" />
                                <span className="text-sm text-gray-500">Loading teams…</span>
                              </div>
                            ) : accessOptions.departments.length === 0 ? (
                              <div className="text-center py-8 px-4 rounded-lg bg-gray-50 border border-gray-100">
                                <BuildingOffice2Icon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">
                                  No teams yet. Create teams in Organization to share assessments.
                                </p>
                              </div>
                            ) : (
                              <div className="grid gap-2">
                                {accessOptions.departments.map((dept) => (
                                  <label
                                    key={dept._id}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                      selectedSharedDepartmentIds.has(dept._id)
                                        ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                                        : 'border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedSharedDepartmentIds.has(dept._id)}
                                      onChange={() => toggleSharedDepartment(dept._id)}
                                      className="sr-only"
                                    />
                                    <div
                                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                        selectedSharedDepartmentIds.has(dept._id)
                                          ? 'border-[#007BFF] bg-[#007BFF]'
                                          : 'border-gray-300 bg-white'
                                      }`}
                                    >
                                      {selectedSharedDepartmentIds.has(dept._id) && (
                                        <svg
                                          className="w-3 h-3 text-white"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                    <BuildingOffice2Icon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setCreatorChoice(null);
                            setCurrentStep(-1);
                          }}
                          className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            // Hide the visibility overlay AND advance to the next
                            // real step. Previously only setShowVisibilityStep(false)
                            // ran, which left currentStep at -2 with no matching
                            // content block → blank pane (regression). Restore the
                            // original step advancement.
                            setShowVisibilityStep(false);
                            setCurrentStep(showChooseMethodStep ? 0 : 1);
                          }}
                          className="rounded-lg bg-[#007BFF] px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0062CC]"
                          data-testid="access-level-continue-btn"
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 0 && (
              <div className="min-h-full flex flex-col pb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid="choose-assessment-type-heading">Choose Assessment Type</h3>
                <CreationModeStep
                  phase={step0Phase}
                  onSelectMode={handleSelectCreationMode}
                  onSelectCategory={handleSelectAssessmentCategory}
                />
                {!hideExternalCreationLink && !showCreatorSelectionStep && step0Phase === 'method' && (
                  <div className="max-w-2xl w-full mx-auto px-4 pb-6">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">External Creation Link</p>
                          <p className="text-xs text-gray-600 mt-1">
                            Generate a link to open this assessment creation modal quickly.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleGenerateExternalCreationLink}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#007BFF] bg-white border border-[#007BFF]/30 rounded-lg hover:bg-[#007BFF]/5 transition-colors whitespace-nowrap"
                        >
                          <LinkIcon className="w-4 h-4" />
                          Generate Link
                        </button>
                      </div>

                      {externalCreationLink && (
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={externalCreationLink}
                            className="flex-1 min-w-0 px-3 py-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              if (!externalCreationLink) return;
                              try {
                                if (navigator?.clipboard?.writeText) {
                                  await navigator.clipboard.writeText(externalCreationLink);
                                }
                                setExternalLinkCopied(true);
                                setTimeout(() => setExternalLinkCopied(false), 2000);
                                toast.success('Link copied.');
                              } catch (err) {
                                toast.error('Could not copy link. Please copy manually.');
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                            {externalLinkCopied ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 1 && isLibraryFlow && (
              <div className="h-full flex flex-col" data-testid="step-1-metadata">
                {/* Search input */}
                <div className="mb-4 flex-shrink-0 px-2 py-1">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search assessments..."
                        value={librarySearchTerm}
                        onChange={(e) => setLibrarySearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] hover:border-gray-300 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={libraryCategoryFilter}
                        onChange={(e) => setLibraryCategoryFilter(e.target.value)}
                        className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] text-gray-700"
                      >
                        <option value="">All categories</option>
                        {['Sales', 'Software Engineering', 'Marketing', 'General', 'IT', 'Engineering', 'Data & Analytics', 'Product Management', 'Recruiting', 'Customer Success', 'Operations', 'Finance', 'People & HR', 'Communication', 'Customer Support', 'Design & UX', 'Accounting', 'Legal', 'Healthcare', 'Education & Training', 'Administration', 'Project Management', 'Business Development', 'Supply Chain & Logistics', 'Quality Assurance', 'Cybersecurity'].map(
                          (c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          )
                        )}
                      </select>
                      <select
                        value={libraryDifficultyFilter}
                        onChange={(e) => setLibraryDifficultyFilter(e.target.value)}
                        className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] text-gray-700"
                      >
                        <option value="">All difficulties</option>
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                        <option value="Expert">Expert</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Assessment list */}
                {loadingLibraryAssessments ? (
                  <div className="flex flex-col items-center justify-center py-16 flex-1">
                    <div className="h-10 w-10 animate-spin rounded-full border-3 border-gray-200 border-t-[#007BFF]" />
                    <p className="mt-4 text-sm text-gray-600">Loading assessments...</p>
                  </div>
                ) : libraryAssessments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 border border-dashed border-gray-200 rounded-xl bg-gray-50/50 flex-1">
                    <svg className="h-12 w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">No assessments in library</h4>
                    <p className="text-xs text-gray-500">Create assessments to see them here.</p>
                  </div>
                ) : (() => {
                  let filteredAssessments = libraryAssessments;

                  // 1. Apply Screening Rules (if configured)
                  const allowedTypes = user?.screeningRules?.allowedAssessmentTypes || [];
                  if (allowedTypes.length > 0) {
                    filteredAssessments = filteredAssessments.filter(a => {
                      // Multi-module
                      if (allowedTypes.includes('multi_module') && a.isMultiModule) return true;
                      // Standard assessments (not multi-module)
                      if (allowedTypes.includes('standard') && !a.isMultiModule) return true;
                      return false;
                    });
                  }

                  // 2. Apply Search and Dropdown Filters
                  filteredAssessments = filteredAssessments.filter(a => {
                    let matches = true;
                    if (librarySearchTerm.trim()) {
                      const term = librarySearchTerm.toLowerCase();
                      matches = matches && (
                        (a.name || a.title || '').toLowerCase().includes(term) ||
                        (a.category || '').toLowerCase().includes(term)
                      );
                    }
                    if (libraryCategoryFilter) {
                      matches = matches && (a.category === libraryCategoryFilter || a.category?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) === libraryCategoryFilter);
                    }
                    if (libraryDifficultyFilter) {
                      matches = matches && (a.difficulty === libraryDifficultyFilter);
                    }
                    return matches;
                  });

                  if (filteredAssessments.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-16 border border-dashed border-gray-200 rounded-xl bg-gray-50/50 flex-1">
                        <svg className="h-10 w-10 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">No matching assessments</h4>
                        <p className="text-xs text-gray-500">Try a different search term.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="flex-1 overflow-hidden border border-gray-200 rounded-xl flex flex-col">
                      <div className="grid grid-cols-[2.25rem_1fr_4rem_4.5rem_5.5rem] gap-x-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                        <span />
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Title</span>
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Questions</span>
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Duration</span>
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Category</span>
                      </div>

                      <div className="flex-1 overflow-y-auto">
                        {filteredAssessments.map((assessment, index) => {
                          const isSelected = selectedLibraryAssessment?._id === assessment._id;
                          const questionCount = assessment.isMultiModule
                            ? (assessment.noOfQuestions || assessment.numberOfQuestions || assessment.modules?.reduce((sum, m) => sum + (m.questionsToPick || m.libraryTestId?.numberOfQuestions || m.libraryTestId?.questions?.length || (Array.isArray(m.questionBank) ? m.questionBank.length : 0) || 0), 0) || 0)
                            : (assessment.numberOfQuestions || assessment.noOfQuestions || assessment.questions?.length || 0);
                          const difficulty = assessment.difficulty;
                          const category = assessment.category;
                          const duration = assessment.estimatedTime || assessment.duration;

                          return (
                            <button
                              key={assessment._id}
                              type="button"
                              data-testid="library-assessment-item"
                              onClick={() => setSelectedLibraryAssessment(assessment)}
                              className={`w-full grid grid-cols-[2.25rem_1fr_4rem_4.5rem_5.5rem] gap-x-3 items-center px-4 py-3 text-left transition-all duration-150 ${index !== 0 ? 'border-t border-gray-100' : ''
                                } ${isSelected
                                  ? 'bg-[#007BFF]/5'
                                  : 'hover:bg-gray-50'
                                }`}
                            >
                              <div className="flex justify-center">
                                <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#007BFF] bg-[#007BFF]' : 'border-gray-300'
                                  }`}>
                                  {isSelected && (
                                    <div className="w-2 h-2 rounded-full bg-white" />
                                  )}
                                </div>
                              </div>

                              <div className="min-w-0 overflow-hidden">
                                <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap scrollbar-hidden w-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                  <style dangerouslySetInnerHTML={{
                                    __html: `
                                    .scrollbar-hidden::-webkit-scrollbar {
                                      display: none !important;
                                    }
                                  ` }} />
                                  <span className={`text-sm font-medium shrink-0 ${isSelected ? 'text-[#007BFF]' : 'text-gray-900'
                                    }`}>
                                    {assessment.name || assessment.title}
                                  </span>
                                  {assessment.isRubricBased && (
                                    <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-100 text-purple-700">
                                      Rubric Assessment
                                    </span>
                                  )}
                                  {assessment.isMultiModule && !assessment.isRubricBased && (
                                    <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-700">
                                      Multi Module
                                    </span>
                                  )}
                                </div>
                                {assessment.description && (
                                  <div className="overflow-x-auto whitespace-nowrap scrollbar-hidden mt-0.5 w-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                    <p className="text-[11px] text-gray-400 leading-tight">{assessment.description}</p>
                                  </div>
                                )}
                              </div>

                              <span className="text-xs text-gray-600 text-center tabular-nums">
                                {questionCount}
                              </span>

                              <span className="text-xs text-gray-600 text-center tabular-nums">
                                {duration ? `${duration}m` : '—'}
                              </span>

                              <span className="text-xs text-gray-500 text-center truncate" title={category || ''}>
                                {category ? category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {currentStep === 1 && !isLibraryFlow && (
              isCodingAssignmentMode ? (
                <AdvancedAssessmentStep
                  registerContinue={setAdvancedContinueSafe}
                  onValidityChange={setIsAdvancedValid}
                  modalBackRef={advancedModalBackRef}
                  onFlowStepChange={setAdvancedFlowStep}
                  onPrimaryLabelChange={setAdvancedPrimaryLabel}
                  onPassageReadingSelected={() => setIsPassageReadingMode(true)}
                  onParagraphReadingSelected={() => setIsParagraphReadingMode(true)}
                  onMultiModuleSelected={() => setIsMultiModuleMode(true)}
                  onRubricBasedSelected={() => setIsRubricBasedMode(true)}
                  onConfigured={handleAdvancedConfigured}
                />
              ) : isParagraphReadingMode ? (
                <ParagraphReadingCreate
                  isModal={true}
                  jobPostingId={jobPostingId}
                  screeningName={screeningName?.trim() || ''}
                  registerSubmit={setDetailsSubmitSafe}
                  onLoadingChange={setDetailsLoading}
                  onGenerationStart={setPendingGenerationCount}
                  onValidationChange={setIsFormValidSafe}
                  onGeneratedSections={handleGeneratedSections}
                  onCreated={handleCreated}
                  initialDraft={detailsDraft}
                  onDraftChange={setDetailsDraft}
                />
              ) : isRubricBasedMode ? (
                <RubricBasedAssessmentCreate
                  isModal={true}
                  jobPostingId={jobPostingId}
                  screeningName={screeningName?.trim() || ''}
                  registerSubmit={setDetailsSubmitSafe}
                  onLoadingChange={setDetailsLoading}
                  onGenerationStart={setPendingGenerationCount}
                  onValidationChange={setIsFormValidSafe}
                  onGeneratedSections={handleGeneratedSections}
                  onCreated={handleCreated}
                  initialDraft={detailsDraft}
                  onDraftChange={setDetailsDraft}
                />
              ) : isMultiModuleMode ? (
                <MultiModuleCreate
                  isModal={true}
                  jobPostingId={jobPostingId}
                  screeningName={screeningName?.trim() || ''}
                  registerSubmit={setDetailsSubmitSafe}
                  onLoadingChange={setDetailsLoading}
                  onGenerationStart={setPendingGenerationCount}
                  onValidationChange={setIsFormValidSafe}
                  onGeneratedSections={handleGeneratedSections}
                  onCreated={handleCreated}
                  initialDraft={detailsDraft}
                  onDraftChange={setDetailsDraft}
                />
              ) : (
                <AssessmentCreate
                  isModal={true}
                  creationMode={isPassageReadingMode ? CREATION_MODE.AI : creationMode}
                  jobPostingId={jobPostingId}
                  screeningName={screeningName?.trim() || ''}
                  registerSubmit={setDetailsSubmitSafe}
                  onLoadingChange={setDetailsLoading}
                  onGenerationStart={setPendingGenerationCount}
                  onValidationChange={setIsFormValidSafe}
                  onGeneratedSections={handleGeneratedSections}
                  onCreated={handleCreated}
                  initialDraft={detailsDraft}
                  onDraftChange={setDetailsDraft}
                  isPassageReadingMode={isPassageReadingMode}
                />
              )
            )}

            {currentStep === 2 && (libraryImportLoading || viewOnlyLoading || editLibraryLoading) && (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[#007BFF]" />
                <p className="mt-4 text-sm font-medium text-gray-700">
                  {viewOnlyLoading || editLibraryLoading
                    ? 'Loading assessment...'
                    : 'Importing assessment from library...'}
                </p>
                <p className="mt-1.5 text-xs text-gray-500">This may take a moment</p>
              </div>
            )}

            {currentStep === 2 && !libraryImportLoading && !viewOnlyLoading && !editLibraryLoading && (
              (isEditLibraryMultiModule || isViewOnlyMultiModule || isLibraryImportMultiModule || isEditLibraryRubricBased || isViewOnlyRubricBased || isLibraryImportRubricBased) ? (
                (isEditLibraryRubricBased || isViewOnlyRubricBased || isLibraryImportRubricBased) ? (
                  <RubricBasedAssessmentCreate
                    isModal
                    modules={
                      (isEditLibraryRubricBased
                        ? editLibraryTest?.modules
                        : isViewOnlyRubricBased
                          ? viewOnlyTest?.modules
                          : importedLibraryTest?.modules) || []
                    }
                    initialDraft={detailsDraft}
                    onDraftChange={setDetailsDraft}
                    onModulesChange={() => {}}
                    onValidationChange={setIsFormValidSafe}
                    viewOnly={!isEditLibraryRubricBased}
                  />
                ) : (
                  <MultiModuleCreate
                    isModal
                    modules={
                      (isEditLibraryMultiModule
                        ? editLibraryTest?.modules
                        : isViewOnlyMultiModule
                          ? viewOnlyTest?.modules
                          : importedLibraryTest?.modules) || []
                    }
                    initialDraft={detailsDraft}
                    onDraftChange={setDetailsDraft}
                    onModulesChange={() => {}}
                    onValidationChange={setIsFormValidSafe}
                    viewOnly={true}
                    libraryOrgId={libraryOrgId}
                  />
                )
              ) : (
                <AssessmentBuild
                  key={buildRefreshKey}
                  isModal
                  creationMode={creationMode}
                  assessmentId={assessmentId}
                  jobPostingId={jobPostingId}
                  initialAiSections={aiSections}
                  initialShuffleQuestionsEnabled={questionShuffleEnabled}
                  onShuffleChange={syncShuffleState}
                  assessmentFormData={assessmentFormData}
                  registerContinue={isViewOnly || isLibraryImport || isLibraryFlow || isEditLibrary ? null : setBuildContinue}
                  registerSaveQuestions={isViewOnly || isLibraryImport || isLibraryFlow || isEditLibrary ? null : setBuildSaveQuestions}
                  onQuestionsChange={isViewOnly || isLibraryImport || isLibraryFlow ? null : setQuestionsData}
                  onDurationChange={isViewOnly || isLibraryImport || isLibraryFlow ? null : setAssessmentDuration}
                  onRegenerateQuestions={isViewOnly || isLibraryImport || isLibraryFlow || isEditLibrary ? null : handleRegenerateQuestions}
                  regeneratingQuestions={regeneratingQuestions}
                  onBack={isLibraryImport || isViewOnly || isLibraryFlow || isEditLibrary ? null : () => setCurrentStep(1)}
                  onContinue={null}
                  viewOnly={isViewOnly || isLibraryImport || isLibraryFlow}
                  initialQuestions={questionsData}
                />
              )
            )}

            {currentStep === 3 && (aiSections || isManualMode || isAdvancedMode || isLibraryImport || isLibraryFlow) && (
              <AssessmentProctoringStep
                value={proctoringState}
                onChange={setProctoringState}
              />
            )}

            {currentStep === 4 && !skipInviteStep && (aiSections || isManualMode || isAdvancedMode || isLibraryImport || isLibraryFlow) && (
              <AssessmentInvite
                isModal
                assessmentId={assessmentId}
                jobPostingId={jobPostingId}
                assessmentFormData={assessmentFormData}
                registerFinish={setInviteFinish}
                saveQuestionsFn={buildSaveQuestions}
                onBack={() => setCurrentStep(3)}
                onDone={async () => {
                  close(true);
                }}
              />
            )}
          </div>

          {/* Footer Navigation */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white absolute bottom-0 left-0 right-0">
            <div className="flex items-center justify-between gap-4">
              {currentStep > 1 && currentStep !== 2 && currentStep !== 3 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (currentStep === 4) setCurrentStep(3);
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  Back
                </button>
              ) : currentStep === 0 && step0Phase === 'method' ? (
                <button
                  type="button"
                  onClick={() => {
                    setAssessmentCategory(null);
                    setCreationMode(null);
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  Back
                </button>
              ) : currentStep === 1 && (showChooseMethodStep || (isCodingAssignmentMode && advancedFlowStep > 0) || isPassageReadingMode || isParagraphReadingMode || isMultiModuleMode || isRubricBasedMode) ? (
                <button
                  type="button"
                  onClick={() => {
                    if (isPassageReadingMode) {
                      setIsPassageReadingMode(false);
                      setAssessmentCategory('advanced');
                      setCreationMode(CREATION_MODE.ADVANCED);
                      return;
                    }
                    if (isParagraphReadingMode) {
                      setIsParagraphReadingMode(false);
                      setAssessmentCategory('advanced');
                      setCreationMode(CREATION_MODE.ADVANCED);
                      return;
                    }
                    if (isMultiModuleMode) {
                      setIsMultiModuleMode(false);
                      setAssessmentCategory('advanced');
                      setCreationMode(CREATION_MODE.ADVANCED);
                      return;
                    }
                    if (isRubricBasedMode) {
                      setIsRubricBasedMode(false);
                      setAssessmentCategory('advanced');
                      setCreationMode(CREATION_MODE.ADVANCED);
                      return;
                    }
                    if (isAdvancedMode && typeof advancedModalBackRef.current === 'function') {
                      if (advancedModalBackRef.current()) return;
                    }
                    if (showChooseMethodStep) {
                      if (isAdvancedMode) {
                        setAssessmentCategory(null);
                      }
                      setCreationMode(null);
                      setCurrentStep(0);
                    }
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  Back
                </button>
              ) : (
                <div />
              )}

              {currentStep === 0 && (
                <div />
              )}

              {currentStep === 1 && isLibraryFlow && (
                <button
                  type="button"
                  onClick={() => {
                    if (selectedLibraryAssessment) {
                      setActiveLibraryTestId(selectedLibraryAssessment._id);
                      setCurrentStep(2);
                    }
                  }}
                  disabled={!selectedLibraryAssessment || libraryImportLoading}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Continue
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}

              {currentStep === 1 && !isLibraryFlow && (
                <button
                  type="button"
                  data-testid="generate-questions-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isCodingAssignmentMode) {
                      if (!isAdvancedValid) {
                        toast.error('Please select at least one template and complete the review step.');
                        return;
                      }
                      if (typeof advancedContinue === 'function' && isAdvancedValid) {
                        Promise.resolve(advancedContinue()).then((res) => {
                          if (res && typeof res === 'object' && res.finish === true) {
                            // Use snapshot: state from onConfigured is not committed until
                            // after this microtask, so the finish handler would see stale null.
                            handleFinishAndPublish({
                              formData: res.formData,
                              questions: res.questions,
                              assessmentDuration: res.formData?.duration,
                            });
                          }
                        });
                      }
                      return;
                    }
                    if (!isFormValid && !isParagraphReadingMode && !isMultiModuleMode) {
                      return;
                    }
                    const submitFn = detailsSubmitRef.current || detailsSubmit;
                    if (typeof submitFn === 'function' && !detailsLoading) {
                      Promise.resolve(submitFn()).then((res) => {
                        if (res && typeof res === 'object' && res.finish === true) {
                          handleFinishAndPublish({
                            formData: res.formData,
                            questions: res.questions,
                            assessmentDuration: res.formData?.duration,
                          });
                        }
                      });
                    }
                  }}
                  disabled={
                    isCodingAssignmentMode
                      ? !advancedContinue
                      : isMultiModuleMode
                        ? (!detailsSubmit || !isFormValid || detailsLoading)
                        : isParagraphReadingMode
                          ? (!detailsSubmit || detailsLoading)
                          : (!detailsSubmit || !isFormValid || detailsLoading)
                  }
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {!isCodingAssignmentMode && detailsLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent shrink-0" />
                      <span className="text-left">
                        {isParagraphReadingMode || isMultiModuleMode || isRubricBasedMode
                          ? 'Creating Assessment…' 
                          : `Generating ${pendingGenerationCount != null ? pendingGenerationCount : '…'} question${pendingGenerationCount !== 1 ? 's' : ''}…`}
                      </span>
                    </>
                  ) : (
                    <>
                      {isCodingAssignmentMode ? advancedPrimaryLabel : (isParagraphReadingMode || isMultiModuleMode || isPassageReadingMode || isRubricBasedMode) ? 'Create Assessment' : isManualMode ? 'Create Questions' : 'Generate Questions'}
                      {!isCodingAssignmentMode && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      )}
                    </>
                  )}
                </button>
              )}

              {currentStep === 2 && (
                <div className="flex items-center gap-3">
                  {isViewOnly ? (
                    <button
                      type="button"
                      onClick={() => onClose?.()}
                      className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Close
                    </button>
                  ) : isEditLibrary ? (
                    <>
                      <button
                        type="button"
                        onClick={requestClose}
                        disabled={editLibrarySaving}
                        className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all duration-200 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveLibraryEdit}
                        disabled={
                          editLibrarySaving ||
                          editLibraryLoading ||
                          (isEditLibraryMultiModule || isEditLibraryRubricBased
                            ? !isFormValid
                            : (!questionsData?.length || !assessmentFormData?.title?.trim())
                          )
                        }
                        className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        {editLibrarySaving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent shrink-0" />
                            Saving…
                          </>
                        ) : (
                          <>
                            <CheckIcon className="w-4 h-4" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </>
                  ) : libraryOnly ? (
                    <button
                      type="button"
                      data-testid="publish-assessment-btn"
                      onClick={() => {
                        if (hasSavedToLibrary) {
                          toast('Already saved to library. Find it under Library → Assessments Library.', { icon: '✓' });
                          return;
                        }
                        handleSaveToLibrary();
                      }}
                      disabled={!assessmentFormData?.title || (!questionsData?.length && assessmentCategory !== 'advanced') || hasSavedToLibrary || savingToLibrary}
                      aria-busy={savingToLibrary}
                      className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      {savingToLibrary ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Saving...
                        </>
                      ) : hasSavedToLibrary ? (
                        'Saved to library'
                      ) : (
                        'Save Assessment'
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={async () => {
                          if (isLibraryImport || isLibraryFlow) {
                            setCurrentStep(3);
                            return;
                          }
                          if (typeof buildContinue === 'function') {
                            const result = await buildContinue();
                            if (result === true) {
                              // Show save to library popup before continuing
                              if (!hasSavedToLibrary && assessmentFormData?.title && questionsData?.length) {
                                setShowSaveToLibraryConfirm(true);
                              } else {
                                setCurrentStep(3);
                              }
                            }
                          } else if (assessmentFormData?.title && questionsData?.length) {
                            // Show save to library popup before continuing
                            if (!hasSavedToLibrary) {
                              setShowSaveToLibraryConfirm(true);
                            } else {
                              setCurrentStep(3);
                            }
                          }
                        }}
                        disabled={!buildContinue && !isLibraryImport && !isLibraryFlow && !(assessmentFormData?.title && questionsData?.length)}
                        className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        Continue
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              )}

              {currentStep === 3 && (
                <div className="flex items-center gap-3">
                  {!skipInviteStep && (
                    <button
                      type="button"
                      onClick={() => setCurrentStep(4)}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all duration-200"
                    >
                      Skip
                    </button>
                  )}
                  {skipInviteStep ? (
                    <div className="flex flex-col items-end gap-1">
                      <button
                        type="button"
                        onClick={handleFinishAndPublish}
                        disabled={!assessmentFormData || finishing}
                        data-testid="create-assessment-final-btn"
                        className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        {finishing ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Creating...
                          </>
                        ) : (
                          <>
                            <CheckIcon className="w-4 h-4" />
                            {hasNextStep ? 'Create Assessment & Continue' : 'Create assessment'}
                          </>
                        )}
                      </button>
                      {hasNextStep && (
                        <p className="text-xs text-gray-500 pr-1">
                          Interview configuration will open next
                        </p>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setCurrentStep(4)}
                      className="px-5 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Continue
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Save to Library confirmation modal */}
          {showSaveToLibraryConfirm && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 transition-all duration-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Save to Assessment Library?</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Click Yes to save this assessment and its questions for future screenings.<br />
                  You can find it later under <strong>Library → Assessments Library</strong> in the sidebar.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (savingToLibrary) return;
                      setShowSaveToLibraryConfirm(false);
                      // Skip saving and continue to next step (for non-libraryOnly flow)
                      if (!libraryOnly) {
                        setCurrentStep(3);
                      }
                    }}
                    disabled={savingToLibrary}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-all duration-200"
                  >
                    Continue without saving
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveToLibrary}
                    disabled={savingToLibrary}
                    data-testid="save-to-library-btn"
                    className="px-4 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:opacity-50 flex items-center gap-2 transition-all duration-200"
                  >
                    {savingToLibrary ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save to library'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Discard confirmation modal (library flows) */}
          {showDiscardConfirm && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 transition-all duration-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3" data-testid="discard-modal-heading">Discard assessment setup?</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Click Yes to discard this assessment configuration and close the wizard. Any unsaved
                  changes and generated questions will be lost.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDiscardConfirm(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setShowDiscardConfirm(false);
                      await close();
                    }}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] transition-all duration-200"
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentCreateFlowModal;

